import React, { useState, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTranslation } from 'react-i18next';
import { Chip, Accordion, AccordionItem, Input, Spinner, Modal, ModalContent, ModalHeader, ModalBody, Button, Tooltip } from '@nextui-org/react';
import { SourceJsonModal, filterCkDataForSource } from './SourceJsonModal';
import { Icon } from '@iconify/react';
import { formatPercentage, formatNumber, formatCoverageDate, parseScore, conversationHistoryTimezone, formatCurrency } from '../utils/dateutils';
import {
    ActionScoreDisplay,
    parseAndFormatScore,
    ClaimScoreHighlightField,
    useScoreColor,
    useRenderScoreChip,
} from './claimScoreRendererShared';
import {
    getActionOutputV2Payload,
    getV2SourcesTemplateData,
    getV2StopGates,
    resolveV2StopGateInfo,
    parseV2StopGatePath,
    buildV2TopLevelAccordionEntries,
    formatSectionTitle,
    getDefaultV2ExpansionKeys,
    isScoredNode,
    resolveOrderedKeys,
} from './actionOutputV2Utils';
import { useClaimScoreActionOutputV2TopLevelItems, renderV2TopLevelBlocks, isVirtualInspectionAgentName } from './ClaimScoreActionOutputV2Renderer';
import { VirtualInspectionSorDetails, readVirtualInspectionMessage, readVirtualInspectionSorDetails } from './VirtualInspectionSorDetails';
import { ErrorDisplay } from './ErrorDisplay';
import {
    extractRendererData,
    resolveActionOutputForAgent,
    extractReceivedClaimDataFromClaimScoreData,
    normalizeVhrExplanation
} from './rendererUtils';
import { RendererHeader } from './RendererHeader';
import { ScoreCard } from './ScoreCard';
import { RendererTabs } from './RendererTabs';
import { FullScreenModal } from './FullScreenModal';
import { MarkdownContent } from './AISummaryAccordion';
import { PredictionExplainerInfoIcon } from './PredictionExplainer';
import { useTypedSelector, useGetAiWorkerUsageByIdMutation, useGetAgentKowledgeSourceMutation, useLazyGetAiWorkerByIdQuery, useLazyGetTemplateUsagePdfQuery, getValidClaimIntakeBearerToken} from '@circuitry-ai/doc-data';
import { UXGenieTemplateRenderer } from '../template/DynamicJSXRenderer';
import { ErrorBoundaryCatch } from '../utiluicomponents/ErrorBoundaryCatch';
import { CtyButton } from '../button/CtyButton';
import { ApplicationType, applicationType } from '../utils/constants';
import { AttachmentMediaWithFallback, isAudioExtension, isVideoExtension } from './claimScoreAttachmentMedia';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { formatKeyForDisplay, openUxGenieTemplateInNewTab } from '../utils/commonutils';
import { downloadFileFromUrl } from '../utils/conversationDownloadUtils';
import { initClaimIntakeSession } from '../claim-intake/claimIntakeAgentApi';
import { requireAgentsStreamBaseUrl } from '../claim-intake/claimScoreAgentApi';
import { useUxgenieTemplateHtmlResolver } from '../template/useUxgenieTemplateHtmlResolver';
import { HtmlTemplateFrame } from '../template/UxgenieTemplateContentView';
import { useCheckApplicationResource } from '../utiluicomponents/useCheckApplicationResource';
import { useSorApi } from '../hooks/useSorApi';
import { isSorEnabled } from '../sor/sorTokenStore';
import { renderMap } from './claimScoreGeoMap';

declare global {
    interface ImportMeta {
        env: Record<string, string>;
    }
}

export interface ClaimScoreGeneratorRendererProps {
    claimData: any;
    onButtonAction?: (action: string, buttonData: any) => void;
    showActionButtons?: boolean;
    className?: string;
    isModal?: boolean;
    source?: 'agent_history' | 'actions';
    feedbackComponent?: React.ReactNode;
    knowledgeComponent?: React.ReactNode;
    onRefresh?: () => void;
    /** When provided, "Full View" delegates to this instead of the built-in modal (e.g. widget host opening a real fullscreen tab). */
    onRequestFullScreen?: () => void;
}

/** `PartialSubmit` → `Partial Submit`, `InReview` → `In Review`. */
const formatCamelCaseLabel = (value: string): string =>
    value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

const INSPECTION_REVIEW_TYPE = 'inspection_review';

/** The agent score as a number and as the `NN%` label the score card shows (null when there is no score). */
const getAgentScoreDisplay = (score: number | string | null | undefined): { scoreValue: number; displayScore: string | null } => {
    if (typeof score === 'number' && Number.isFinite(score)) {
        return { scoreValue: score, displayScore: `${Math.round(score)}%` };
    }
    const scoreValue = parseScore(score as number | string);
    const hasScore = typeof score === 'string' || Boolean(score);
    return { scoreValue, displayScore: hasScore ? `${Math.round(scoreValue)}%` : null };
};

const TW_CURRENCY_KEYS = new Set([
    'labor_limit',
    'policy_tire_limit',
    'tire_repair_limit',
    'policy_wheel_limit',
    'wheel_repair_limit',
    'tire_replacement_limit',
    'wheel_replacement_limit',
    'cosmetic_wheel_repair_limit',
    'policy_limit',
]);

interface MsrpSourceGridRow {
    rowId: number;
    sourceRaw: string;
    sourceDisplay: string;
    isCkOem: boolean;
    isForte: boolean;
    isTealRow: boolean;
    hasCkData: boolean;
    hasForteData: boolean;
    hasTealData: boolean;
    valueDisplay: string | number;
    coreValueDisplay: string | number;
}

type MsrpSourceGridContext = {
    setSourceJsonModal: (p: { title: string; data: any }) => void;
    ckSourceData: any;
    forteSourceData: any;
    tealSourceData: any;
};

const SourceInfoButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <Tooltip content={label} placement="top" showArrow>
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 text-default-500 hover:text-default-700"
            aria-label={label}
        >
            <Icon icon="mdi:information-outline" className="w-4 h-4" />
        </button>
    </Tooltip>
);

const MsrpSourceAgGridCell: React.FC<ICellRendererParams<MsrpSourceGridRow>> = (props) => {
    const { t } = useTranslation();
    const d = props.data;
    const ctx = props.context as MsrpSourceGridContext | undefined;
    if (!d || !ctx) return null;
    const { setSourceJsonModal, ckSourceData, forteSourceData, tealSourceData } = ctx;
    return (
        <span className="inline-flex items-center gap-1 capitalize text-default-500 max-w-full">
            <span className="truncate min-w-0">{d.sourceDisplay}</span>
            {d.isCkOem && <SourceInfoButton label={`${t('View')} ${d.sourceDisplay} ${t('info')}`} onClick={() => d.hasCkData && setSourceJsonModal({ title: 'C_&_K_source', data: filterCkDataForSource(ckSourceData, d.sourceRaw) })} />}
            {d.isForte && <SourceInfoButton label={`${t('View')} forte ${t('info')}`} onClick={() => d.hasForteData && setSourceJsonModal({ title: 'forte_source', data: forteSourceData })} />}
            {d.isTealRow && <SourceInfoButton label={`${t('View')} teal ${t('info')}`} onClick={() => d.hasTealData && setSourceJsonModal({ title: 'Teal_source', data: tealSourceData })} />}
        </span>
    );
};

interface SrtSourceGridRow {
    rowId: number;
    sourceDisplay: string;
    srtDisplay: string | number;
    hasInfo: boolean;
    infoData: any;
}

const SrtSourceAgGridCell: React.FC<ICellRendererParams<SrtSourceGridRow>> = (props) => {
    const { t } = useTranslation();
    const d = props.data;
    const ctx = props.context as { setSourceJsonModal: (p: { title: string; data: any }) => void } | undefined;
    if (!d || !ctx) return null;
    return (
        <span className="inline-flex items-center gap-1 capitalize text-default-500 whitespace-nowrap">
            {d.sourceDisplay}
            {d.hasInfo && <SourceInfoButton label={t('View info')} onClick={() => ctx.setSourceJsonModal({ title: `${d.sourceDisplay} Info`, data: d.infoData })} />}
        </span>
    );
};

const GRID_DEFAULT_COL_DEF: ColDef = {
    sortable: false,
    resizable: true,
    suppressMovable: true,
    headerClass: 'text-left font-medium text-default-700',
};

const ClaimScoreGeneratorRenderer: React.FC<ClaimScoreGeneratorRendererProps> = ({
    claimData,
    onButtonAction,
    showActionButtons = true,
    className = '',
    isModal = false,
    source = 'agent_history',
    feedbackComponent,
    knowledgeComponent,
    onRefresh,
    onRequestFullScreen
}) => {
    const { t } = useTranslation();
    const token = useTypedSelector((state) => state.auth.token) as string | null;
    // Agent-config driven score color (falls back to 85/60 defaults when no provider/config).
    const getScoreColor = useScoreColor();
    const renderScoreChip = useRenderScoreChip();
    const [inspectionReviewPending, setInspectionReviewPending] = useState<'Completed' | 'Cancelled' | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [requestNestedKeys, setRequestNestedKeys] = useState<Set<string>>(new Set());
    const [supportingNestedKeys, setSupportingNestedKeys] = useState<Set<string>>(new Set());
    const [attachmentAnalysisNestedKeys, setAttachmentAnalysisNestedKeys] = useState<Set<string>>(new Set());
    const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
    const [isTemplateFullScreenOpen, setIsTemplateFullScreenOpen] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isTemplateModalFullscreen, setIsTemplateModalFullscreen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeFilter, setActiveFilter] = useState<'matches' | 'partial' | 'mismatches' | null>(null);
    const [activeStopGate, setActiveStopGate] = useState<string | null>(null);
    const [stopGateScrollTargetId, setStopGateScrollTargetId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<string>('overview');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [templateFullData, setTemplateFullData] = useState<any>(null);
    const [resolvedTemplateHtml, setResolvedTemplateHtml] = useState<string | null>(null);
    const [resolvedTemplateHtmlUrl, setResolvedTemplateHtmlUrl] = useState<string | null>(null);
    const [sourcesSearchQuery, setSourcesSearchQuery] = useState<string>('');
    const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);
    const [hasInitializedAttachmentExpansion, setHasInitializedAttachmentExpansion] = useState(false);
    const [sourceJsonModal, setSourceJsonModal] = useState<{ title: string; data: any } | null>(null);

    // Get templates from Redux store
    const uxgenieTemplates = useTypedSelector((state) => state.uxgenieSlice?.templates || []);

    // Mutation to fetch full template data
    const [getTemplateById, { isLoading: isLoadingTemplateData }] = useGetAiWorkerUsageByIdMutation();
    const [getAiWorkerById, { isFetching: isFetchingAgentForTemplate }] = useLazyGetAiWorkerByIdQuery();
    const { resolveHtml, resolveHtmlUrl } = useUxgenieTemplateHtmlResolver();
    const [getTemplateUsagePdf] = useLazyGetTemplateUsagePdfQuery();
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const { checkAndProceed: checkDownloadPdf, isChecking: isCheckingDownloadPdf } = useCheckApplicationResource('download_pdf');
    const [getAgentPresignedUrls, { isLoading: isLoadingAgentPresignedUrls }] = useGetAgentKowledgeSourceMutation();
    const [imageScorePresignedUrls, setImageScorePresignedUrls] = useState<Record<string, string>>({});
    const [presignedUrlsFetchFailed, setPresignedUrlsFetchFailed] = useState(false);
    const { getSorToken } = useSorApi();

    // Extract data before early returns (required for hooks)
    const { data, agentScore, duration, entityNumber, timestamp, aiSummary, agentName, actionName } = useMemo(
        () => extractRendererData(claimData),
        [claimData]
    );

    const handleInspectionReview = async (nextState: 'Completed' | 'Cancelled') => {
        if (inspectionReviewPending) return;
        const aiworkerId = data?.aiworker_id;
        const aiworkerUsageId = data?.aiworker_usage_id;
        if (!token || !aiworkerId || !aiworkerUsageId) {
            CtyShowToast(checkType.error, t('Usage data is missing'));
            return;
        }
        setInspectionReviewPending(nextState);
        try {
            const session = await initClaimIntakeSession({
                aiworkerId: String(aiworkerId),
                token,
            });
            const baseUrl = requireAgentsStreamBaseUrl();
            const bearer = await getValidClaimIntakeBearerToken(token);
            // The tenant settings turn SOR off; until they load the token is still sent
            const sorToken = isSorEnabled() !== false ? await getSorToken() : undefined;
            const difyResponse = await fetch(`${baseUrl}/dify_stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    Authorization: `Bearer ${bearer}`,
                },
                body: JSON.stringify({
                    payload: {
                        user_input: {
                            aiworker_usage_id: String(aiworkerUsageId),
                            type: INSPECTION_REVIEW_TYPE,
                            state: nextState,
                        },
                        meta_data: { sor_token: sorToken },
                        aiworker_id: session.aiworkerId,
                        session_id: session.sessionId,
                    },
                }),
            });
            if (!difyResponse.ok) throw new Error(`dify stream failed: ${difyResponse.status}`);
            await difyResponse.text();
            await onRefresh?.();
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? t('Operation failed'));
        } finally {
            setInspectionReviewPending(null);
        }
    };

    const shouldRender = useMemo(() => {
        if (source === 'agent_history') {
            const lower = agentName.toLowerCase();
            return lower.includes('claim score') || lower.includes('claim_score') ||
                lower.includes('vhr') || lower.replace(/_/g, ' ').includes('inspection agent');
        }
        return actionName.toLowerCase() === 'lifecycle of claim';
    }, [source, agentName, actionName]);

    const claimScoreData = resolveActionOutputForAgent(
        data?.action_output as Record<string, unknown> | undefined,
        agentName
    ) as any;

    const v2Payload = useMemo(
        () => getActionOutputV2Payload(data?.action_output),
        [data?.action_output]
    );
    const isVirtualInspection = isVirtualInspectionAgentName(agentName);
    const sorInspection = isVirtualInspection ? readVirtualInspectionSorDetails(data) : null;

    const stopGatePath = useMemo(
        () => (activeStopGate ? parseV2StopGatePath(activeStopGate) : null),
        [activeStopGate]
    );

    const { blocks: v2TopBlocks, modal: v2SourceModal } = useClaimScoreActionOutputV2TopLevelItems({
        data: v2Payload ?? { version: 0, explanation: {} },
        enabled: Boolean(v2Payload) && !sorInspection,
        searchQuery,
        activeFilter,
        stopGatePath,
        nestedSelectedKeys: requestNestedKeys,
        onNestedSelectedKeysChange: setRequestNestedKeys,
        timestamp,
        agentName,
        // Reuse the legacy renderer's modal so V2 info-icon clicks open the same modal.
        setSourceJsonModal,
    });

    // Legacy claim object for UXGenie; V2 falls back to sources.*@template when legacy fields are absent.
    const receivedClaimData = useMemo(() => {
        const legacy = extractReceivedClaimDataFromClaimScoreData( claimScoreData, agentName, data?.entity_number );
        if (legacy) return legacy;
        return getV2SourcesTemplateData(v2Payload?.sources) as Record<string, any> | null;
    }, [claimScoreData, agentName, data?.entity_number, v2Payload?.sources]);

    // Render Sources content with claim number (action_output keyed by agent name)
    const sourcesContent = useMemo(() => {
        // Show claim number / VIN when source payload is available
        if (shouldRender) {
            const claimNumber =
                receivedClaimData?.vin ||
                receivedClaimData?.VEHICLE?.[0]?.VIN ||
                receivedClaimData?.claim_no ||
                receivedClaimData?.claim_id ||
                data?.entity_number ||
                'Unknown Claim';
            return (
                <div className="w-full flex flex-col gap-2">
                    <div>
                        <div className="mb-4 px-2">
                            <Input
                                type="text"
                                placeholder="Search by field name, value, or notes..."
                                value={sourcesSearchQuery}
                                onChange={(e) => setSourcesSearchQuery(e.target.value)}
                                startContent={<Icon icon="mdi:magnify" className="text-default-400" style={{ width: '18px', height: '18px' }} />}
                                size="sm"
                                classNames={{
                                    input: "text-sm",
                                    inputWrapper: "border-default-300 h-8"
                                }}
                            />
                        </div>
                        {receivedClaimData && <div className="flex">
                            <CtyButton
                                color="default"
                                variant="light"
                                isLoading={isFetchingAgentForTemplate || isLoadingTemplateData}
                                onPress={() => {
                                    if (Object.keys(receivedClaimData).length === 0) {
                                        CtyShowToast(checkType.error, t('No claim data available.'));
                                        return;
                                    }
                                    if (!selectedTemplateId) {
                                        CtyShowToast(checkType.error, t('No template selected. Please configure a UXGenie template in action settings.'));
                                        return;
                                    }
                                    // Extension: navigate to PAL (uxgenie-view); non-extension: open dialog
                                    if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
                                        void openTemplateInNewTab();
                                    } else {
                                        setIsTemplateModalOpen(true);
                                    }
                                }}
                                startContent={<Icon icon="mdi:eye" width={16} />}
                                className="hover:bg-transparent"
                            >
                                {claimNumber}
                            </CtyButton>
                        </div>}
                    </div>
                    {knowledgeComponent && <div className="mt-2">{knowledgeComponent}</div>}
                </div>
            );
        }

        // For other components: use knowledgeComponent prop (if provided)
        if (knowledgeComponent) {
            return knowledgeComponent;
        }

        // Fallback: No sources available
        return (
            <div className="p-4 text-center text-default-500">
                <Icon icon="mdi:information-outline" className="w-12 h-12 mx-auto mb-2 text-default-400" />
                <p>No source data available</p>
            </div>
        );
    }, [shouldRender, receivedClaimData, selectedTemplateId, knowledgeComponent, sourcesSearchQuery, isFetchingAgentForTemplate, isLoadingTemplateData]);

    const showSourcesTab = !globalThis.location?.href.includes('claim-intake');

    // Reset template state when claim record changes (e.g. user opened a different record in agent history)
    const claimRecordId = data?.aiworker_usage_id ?? '';
    useEffect(() => {
        setActiveTab('overview');
        setSelectedTemplateId('');
        setTemplateFullData(null);
    }, [claimRecordId]);

    // When Sources tab is opened in agent_history: fetch agent by aiworker_id, get uxgenie_template attr_value, then fetch template by that id
    useEffect(() => {
        if (activeTab !== 'sources' || !shouldRender || !data?.aiworker_id || source !== 'agent_history') {
            return;
        }
        const aiworkerId = data.aiworker_id;
        getAiWorkerById(aiworkerId)
            .then((result: any) => {
                const agent = result?.data;
                if (!agent) return;
                const attrs = agent?.aiworkers_attrs ?? [];
                const uxgenieAttr = attrs.find((attr: any) => attr?.attr_name === 'uxgenie_template');
                const templateId = uxgenieAttr?.attr_value?.trim();
                if (templateId) {
                    setSelectedTemplateId(templateId);
                }
            })
            .catch((err) => {
                console.error('Failed to fetch agent for UXGenie template:', err);
            });
    }, [activeTab, shouldRender, data?.aiworker_id, source, getAiWorkerById]);

    // Get selected template metadata (optional, for display purposes)
    const selectedTemplate = useMemo(() => {
        if (!selectedTemplateId) return null;
        return uxgenieTemplates.find((t: any) => t.aiworker_usage_id === selectedTemplateId) || null;
    }, [selectedTemplateId, uxgenieTemplates]);

    // Fetch full template data when template is selected
    useEffect(() => {
        if (selectedTemplateId) {
            getTemplateById(selectedTemplateId)
                .unwrap()
                .then((response) => {
                    // Handle both array and object responses
                    let templateData = response;
                    if (Array.isArray(response) && response.length > 0) {
                        templateData = response[0];
                    } else if (response?.data) {
                        templateData = response.data;
                    }

                    setTemplateFullData(templateData);
                })
                .catch((error) => {
                    console.error('Failed to fetch template data:', error);
                    setTemplateFullData(null);
                });
        }
    }, [selectedTemplateId, getTemplateById]);

    // If the template's HTML was saved to S3 (WAF blocks large inline HTML), resolve
    // a presigned URL for it once the full template record has loaded — loaded directly
    // via iframe `src` when the newer `source_id` field is available, falling back to
    // fetching the raw HTML text for templates saved before that field existed.
    useEffect(() => {
        const outputData = templateFullData?.action_output || templateFullData?.output_data;
        const inlineCode = templateFullData?.action_output?.generated_code || templateFullData?.output_data?.generated_code;
        if (inlineCode || outputData?.output_type !== 'html' || outputData?.storage !== 's3') {
            setResolvedTemplateHtml(null);
            setResolvedTemplateHtmlUrl(null);
            return;
        }
        const aiworker_id = templateFullData?.aiworker_id;
        const aiworker_usage_id = templateFullData?.aiworker_usage_id || selectedTemplateId;
        const sourceId = outputData.source_id;
        if (!aiworker_id || !aiworker_usage_id) return;

        let cancelled = false;
        setResolvedTemplateHtml(null);
        setResolvedTemplateHtmlUrl(null);

        const resolve = sourceId
            ? resolveHtmlUrl({
                aiworker_id,
                aiworker_usage_id,
                source_id: sourceId,
                extension: outputData.html_file_name?.split('.').pop() || 'html',
            }).then((url) => { if (!cancelled) setResolvedTemplateHtmlUrl(url); })
            // Fallback for templates saved before `source_id` was stored on `action_output`
            : resolveHtml({ aiworker_id, aiworker_usage_id, fileName: outputData.html_file_name })
                .then((html) => { if (!cancelled) setResolvedTemplateHtml(html); });

        resolve.catch((error) => console.error('Failed to resolve saved HTML template from storage:', error));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateFullData, selectedTemplateId, resolveHtml, resolveHtmlUrl]);

    const openTemplateInNewTab = async () => {
        let generatedCode = templateFullData?.action_output?.generated_code ||
            templateFullData?.output_data?.generated_code ||
            resolvedTemplateHtml;

        // HTML templates resolved to a presigned `src` (no text fetched, to skip the
        // list-key round trip) still need raw text here — fetch it directly from the
        // URL we already have rather than re-resolving via `resolveHtml`.
        if (!generatedCode && resolvedTemplateHtmlUrl) {
            try {
                const response = await fetch(resolvedTemplateHtmlUrl);
                if (response.ok) generatedCode = await response.text();
            } catch (error) {
                console.error('Failed to fetch resolved HTML template content:', error);
            }
        }

        if (!generatedCode || !receivedClaimData) {
            CtyShowToast(checkType.error, t('Template or claim data is missing'));
            return;
        }
        await openUxGenieTemplateInNewTab(
            { generatedCode, receivedClaimData, agentName },
            {
                onSessionStorageError: () =>
                    CtyShowToast(checkType.error, t('Could not open in new tab')),
            }
        );
    };

    const downloadTemplateAsPdf = async () => {
        const aiworkerUsageId = data?.aiworker_usage_id;
        if (!aiworkerUsageId || !selectedTemplateId) {
            CtyShowToast(checkType.error, t('Usage data is missing'));
            return;
        }
        try {
            setIsDownloadingPdf(true);
            const result = await getTemplateUsagePdf({
                aiworker_usage_id: aiworkerUsageId,
                aiworker_id: data?.aiworker_id
            }).unwrap();
            const presignedUrl = result?.presigned_url;
            if (!presignedUrl) {
                throw new Error('No download URL returned');
            }
            const claimNo = receivedClaimData?.claim_no ?? '';
            const VIN = receivedClaimData?.VEHICLE?.[0]?.VIN ?? '';
            let filename = '';
            if (VIN) {
                filename = `${agentName} - ${VIN}.pdf`;
            } else {
                filename = `${agentName} - ${claimNo}.pdf`;
            }
            await downloadFileFromUrl(presignedUrl, filename);
            CtyShowToast(checkType.success, t('PDF downloaded'));
        } catch (err) {
            console.error('Download PDF failed:', err);
            CtyShowToast(checkType.error, t('Failed to download PDF'));
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    // Render template with data
    const renderTemplatePreview = (isFullscreen = false) => {
        const outputDataForLoadingCheck = templateFullData?.action_output || templateFullData?.output_data;
        const isHtmlTemplate = outputDataForLoadingCheck?.output_type === 'html';
        const isAwaitingHtmlResolution = !!templateFullData
            && !(templateFullData?.action_output?.generated_code || templateFullData?.output_data?.generated_code)
            && isHtmlTemplate
            && outputDataForLoadingCheck?.storage === 's3'
            && !resolvedTemplateHtml
            && !resolvedTemplateHtmlUrl;

        if (isLoadingTemplateData || isAwaitingHtmlResolution) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-default-500">
                    <Spinner size="lg" className="mb-4" />
                    <p>Loading template...</p>
                </div>
            );
        }

        // Check both action_output and output_data for generated_code (API returns different structures),
        // falling back to HTML resolved from S3 storage when the template was saved that way.
        const generatedCode = templateFullData?.action_output?.generated_code ||
            templateFullData?.output_data?.generated_code ||
            resolvedTemplateHtml;

        // An HTML template resolved to a presigned `src` (no text fetched) has no
        // `generatedCode` — that's fine, the iframe loads it directly via the URL.
        if (!generatedCode && !resolvedTemplateHtmlUrl) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-default-500">
                    <Icon icon="mdi:file-document-remove-outline" width={48} className="mb-4 text-default-400" />
                    <p>{!selectedTemplate ? 'No template selected' : 'Template data not loaded yet'}</p>
                </div>
            );
        }

        if (!receivedClaimData || Object.keys(receivedClaimData).length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-default-500">
                    <Icon icon="mdi:file-document-remove-outline" width={48} className="mb-4 text-default-400" />
                    <p>No claim data available for template</p>
                </div>
            );
        }

        // Standalone HTML templates render themselves from data sent via postMessage
        // rather than data baked into JSX — load them in a sandboxed iframe instead of
        // transpiling them as JSX (which is what caused "Template Rendering Error" here).
        if (isHtmlTemplate) {
            return (
                <div
                    className={
                        isFullscreen
                            ? "bg-white overflow-hidden min-h-0 h-full flex-1 w-full max-w-full flex flex-col"
                            : "bg-white  overflow-hidden w-full max-w-full"
                    }
                >
                    <HtmlTemplateFrame
                        code={resolvedTemplateHtml ?? ''}
                        src={resolvedTemplateHtmlUrl ?? undefined}
                        data={receivedClaimData}
                        options={{ claimNumber: data?.entity_number, aiworkerUsageId: data?.aiworker_usage_id }}
                        className={
                            isFullscreen
                                ? "w-full flex-1 min-h-[60vh] border-0 bg-white"
                                : "w-full min-h-[500px] max-h-[600px] border-0 bg-white"
                        }
                    />
                </div>
            );
        }

        // Use the shared renderer (same as the /agents/history view page): it replaces object
        // children (e.g. an API error `{ error, correlationId }`) with readable text instead of
        // letting React throw #31, which a try/catch here cannot catch and crashed the whole page.
        return (
            <div
                className={
                    isFullscreen
                        ? "bg-white p-2 overflow-auto min-h-0 flex-1 w-full max-w-full"
                        : "bg-white p-2 overflow-auto max-h-[600px] w-full max-w-full"
                }
            >
                <div className="w-full max-w-full overflow-x-auto">
                    <ErrorBoundaryCatch
                        key={generatedCode}
                        fallback={
                            <div className="flex flex-col items-center justify-center py-12 text-red-500">
                                <Icon icon="mdi:alert-circle" width={48} className="mb-4" />
                                <p className="font-semibold">Template Rendering Error</p>
                                <p className="text-sm text-default-500 mt-2">Could not render the template with this data.</p>
                            </div>
                        }
                    >
                        <UXGenieTemplateRenderer code={generatedCode} data={receivedClaimData} />
                    </ErrorBoundaryCatch>
                </div>
            </div>
        );
    };

    // Helper function to process error body
    const processErrorBody = (body: any) => {
        if (!body) return null;
        if (typeof body === 'string') {
            try {
                return JSON.parse(body);
            } catch {
                return null;
            }
        }
        return typeof body === 'object' ? body : null;
    };

    const HANDLED_FIRST_ELEMENT_KEYS = new Set([
        'requests', 'claim_score', 'claim_amount', 'image_scores',
        'vehicle_owner_history', 'vehicle_history_report',
        'T&W_check', 'policy_limit_check',
        'received_claim_number', 'received_universal_claim_obj',
        'time_metrics', 'session_id', 'config',
        'source_data', 'pdf_source_ids', 'source_ids', 'sources',
        'vin', 'response_type',
        'error', 'body',
    ]);

    // Extract requests, image_scores and errorData
    const { requests, imageScores, vehicleOwnerHistory, policyLimitCheck, extraTopLevelSections, errorData } = useMemo(() => {
        if (v2Payload) {
            return { requests: [], imageScores: [], vehicleOwnerHistory: null, policyLimitCheck: null, extraTopLevelSections: [] as { key: string; value: any }[], errorData: null };
        }
        if (!claimScoreData) {
            return { requests: [], imageScores: [], vehicleOwnerHistory: null, policyLimitCheck: null, extraTopLevelSections: [] as { key: string; value: any }[], errorData: null };
        }

        const collectExtras = (item: any): { key: string; value: any }[] => {
            if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
            return Object.entries(item)
                .filter(([k, v]) => !HANDLED_FIRST_ELEMENT_KEYS.has(k) && v != null && typeof v === 'object')
                .map(([k, v]) => ({ key: k, value: v }));
        };

        if (Array.isArray(claimScoreData)) {
            const firstItem = claimScoreData[0];
            if (!firstItem) return { requests: [], imageScores: [], vehicleOwnerHistory: null, policyLimitCheck: null, extraTopLevelSections: [] as { key: string; value: any }[], errorData: null };
            return {
                requests: claimScoreData.flatMap((item: any) => item.requests || []),
                imageScores: claimScoreData.flatMap((item: any) => item.image_scores || []),
                vehicleOwnerHistory: claimScoreData.find((item: any) => item.vehicle_owner_history)?.vehicle_owner_history
                    ?? claimScoreData.find((item: any) => item.vehicle_history_report)?.vehicle_history_report
                    ?? null,
                policyLimitCheck: claimScoreData.find((item: any) => item?.["T&W_check"])?.["T&W_check"] || claimScoreData.find((item: any) => item.policy_limit_check)?.policy_limit_check ||null,
                extraTopLevelSections: collectExtras(firstItem),
                errorData: null
            };
        }

        if (claimScoreData.error || claimScoreData.body) {
            const parsedBody = processErrorBody(claimScoreData.body);
            return {
                requests: [],
                imageScores: [],
                vehicleOwnerHistory: null,
                policyLimitCheck: null,
                extraTopLevelSections: [] as { key: string; value: any }[],
                errorData: {
                    error: claimScoreData.error,
                    body: claimScoreData.body,
                    ...(parsedBody && { parsedBody })
                }
            };
        }

        return {
            requests: claimScoreData?.requests || [],
            imageScores: claimScoreData?.image_scores || [],
            vehicleOwnerHistory: claimScoreData?.vehicle_owner_history ?? claimScoreData?.vehicle_history_report ?? null,
            policyLimitCheck: claimScoreData?.["T&W_check"] || claimScoreData?.policy_limit_check || null,
            extraTopLevelSections: collectExtras(claimScoreData),
            errorData: null
        };
    }, [claimScoreData, v2Payload]);

    // Payload to fetch presigned URLs for image_scores that have source_id (stable key to avoid duplicate API calls)
    const imageScorePresignedPayload = useMemo(() => {
        if (!imageScores?.length) return null;
        const sourceIds = imageScores
            .filter((img: any) => img?.source_id)
            .map((img: any) => {
                const fileName = img?.filename || 'image';
                const ext = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
                return { source_id: img.source_id, filename: fileName, file_extension: ext };
            });
        return sourceIds.length > 0 ? { source_ids: sourceIds } : null;
    }, [imageScores]);

    const presignedFetchKeyRef = useRef<string | null>(null);
    const templatePreviewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!imageScorePresignedPayload?.source_ids?.length) return;
        const key = imageScorePresignedPayload.source_ids.map((s: any) => s.source_id).sort().join(',');
        if (presignedFetchKeyRef.current === key) return;
        presignedFetchKeyRef.current = key;

        getAgentPresignedUrls(imageScorePresignedPayload as any)
            .unwrap()
            .then((res: any) => {
                const next: Record<string, string> = {};
                (res?.data || []).forEach((d: any) => {
                    if (d?.source_id && d?.full_source_presigned_url) {
                        next[d.source_id] = d.full_source_presigned_url;
                    }
                });
                setImageScorePresignedUrls(prev => ({ ...prev, ...next }));
            })
            .catch((err: any) => {
                const message = err?.data?.message ?? err?.message ?? t('Failed to load attachment');
                CtyShowToast(checkType.error, message);
                setPresignedUrlsFetchFailed(true);
            });
    }, [imageScorePresignedPayload]);

    // Expand Requests + first nested request accordion by default on initial V2 load
    useEffect(() => {
        if (hasInitializedExpansion) return;
        if (v2Payload?.explanation) {
            const { mainKey, nestedKeys } = getDefaultV2ExpansionKeys(v2Payload);
            if (mainKey) {
                setSelectedKeys((prev) => {
                    if (prev.has(mainKey)) return prev;
                    const newSet = new Set(prev);
                    newSet.add(mainKey);
                    return newSet;
                });
                if (nestedKeys.length > 0) {
                    setRequestNestedKeys((prev) => {
                        const newSet = new Set(prev);
                        for (const k of nestedKeys) newSet.add(k);
                        return newSet;
                    });
                }
                setHasInitializedExpansion(true);
            }
            return;
        }
        if (requests.length > 0) {
            setSelectedKeys(prev => {
                if (!prev.has('request-0')) {
                    const newSet = new Set(prev);
                    newSet.add('request-0');
                    return newSet;
                }
                return prev;
            });
            setHasInitializedExpansion(true);
        }
    }, [requests.length, hasInitializedExpansion, v2Payload]);

    useEffect(() => {
        if (!stopGateScrollTargetId) return undefined;
        const targetId = stopGateScrollTargetId;
        const timer = setTimeout(() => {
            document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setStopGateScrollTargetId(null);
        }, 100);
        return () => clearTimeout(timer);
    }, [stopGateScrollTargetId]);

    // Expand first attachment (Attachment 1) inside Attachment Analysis by default on initial load
    useEffect(() => {
        if (imageScores.length > 0 && !hasInitializedAttachmentExpansion) {
            const firstScore = imageScores[0];
            const firstExplanationObj = (firstScore?.explanation && typeof firstScore.explanation === 'object' && !Array.isArray(firstScore.explanation)) ? firstScore.explanation : null;
            const firstResolvedDescription = firstExplanationObj?.description ?? firstScore?.description ?? '';
            const firstResolvedDetails = firstExplanationObj?.details ?? firstScore?.details ?? {};
            const descriptionHash = firstResolvedDescription.substring(0, 20).replace(/\s+/g, '-') || 'empty';
            const firstAttachmentKey = `image-score-0-${firstScore?.score || 'no-score'}-${descriptionHash}-${JSON.stringify(firstResolvedDetails).substring(0, 10)}`;
            setAttachmentAnalysisNestedKeys(prev => {
                if (!prev.has(firstAttachmentKey)) {
                    const newSet = new Set(prev);
                    newSet.add(firstAttachmentKey);
                    return newSet;
                }
                return prev;
            });
            setHasInitializedAttachmentExpansion(true);
        }
    }, [imageScores.length, hasInitializedAttachmentExpansion]);

    // Helper function to update breakdown based on score
    const updateBreakdown = (breakdown: { matches: number; partial: number; mismatches: number }, score: any) => {
        if (score === undefined || score === null) return;
        const numScore = Math.round(parseScore(score));
        if (numScore >= 85) breakdown.matches++;
        else if (numScore >= 60) breakdown.partial++;
        else breakdown.mismatches++;
    };

    // Calculate overall score for Attachment Analysis
    const attachmentAnalysisScore = useMemo(() => {
        if (!imageScores || imageScores.length === 0) return null;
        const validScores = imageScores
            .map((img: any) => parseScore(img?.score))
            .filter((score: number) => !Number.isNaN(score));
        if (validScores.length === 0) return null;
        return validScores.reduce((a: number, b: number) => a + b, 0) / validScores.length;
    }, [imageScores]);

    // Calculate match breakdown: request actions + top-level scores only (Attachment Analysis, Vehicle History Report, Policy Limit Check)
    const matchBreakdown = useMemo(() => {
        const breakdown = { matches: 0, partial: 0, mismatches: 0 };
        if (v2Payload?.explanation) {
            for (const { sectionValue } of buildV2TopLevelAccordionEntries(
                v2Payload.explanation as Record<string, unknown>,
                v2Payload.order
            )) {
                if (isScoredNode(sectionValue)) updateBreakdown(breakdown, sectionValue.score);
            }
            return breakdown;
        }
        requests.forEach((request: any) => {
            const actions = request?.actions || {};
            for (const actionData of Object.values(actions)) {
                updateBreakdown(breakdown, (actionData as any)?.score);
            }
        });
        if (attachmentAnalysisScore !== null) {
            updateBreakdown(breakdown, attachmentAnalysisScore);
        }
        if (vehicleOwnerHistory?.score !== undefined && vehicleOwnerHistory?.score !== null) {
            updateBreakdown(breakdown, vehicleOwnerHistory.score);
        }
        if (policyLimitCheck?.score !== undefined && policyLimitCheck?.score !== null) {
            updateBreakdown(breakdown, policyLimitCheck.score);
        }
        return breakdown;
    }, [v2Payload, requests, attachmentAnalysisScore, vehicleOwnerHistory, policyLimitCheck]);

    const stopGateItems = useMemo(() => {
        if (!v2Payload) return [];
        return getV2StopGates(v2Payload).map((gate) => {
            const info = resolveV2StopGateInfo(v2Payload, gate);
            return { key: gate, label: info.label, score: info.score };
        });
    }, [v2Payload]);

    // Consolidated action metadata
    const actionMetadata: Record<string, { name: string; icon: string; iconColor: string; subtitle: string }> = {
        'repair_profile': { name: 'Repair Profile', icon: 'mdi:wrench', iconColor: 'text-purple-600', subtitle: 'Cost and frequency analysis' },
        '3Cs_text_analysis': { name: "3C's Analysis", icon: 'mdi:clipboard-text', iconColor: 'text-blue-600', subtitle: 'Complaint, Cause, Correction' },
        'coverage_validation': { name: 'Coverage Validation', icon: 'mdi:shield-check', iconColor: 'text-green-600', subtitle: 'Policy and coverage status' },
        'cost_validation': { name: 'Cost Validation', icon: 'mdi:alert-circle', iconColor: 'text-orange-600', subtitle: 'Cost validation' },
    };

    const formatActionName = (actionKey: string) => {
        return actionMetadata[actionKey]?.name || actionKey
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Averaged action scores across all requests for ScoreCard back (score calculation)
    const scoreCalculationItems = useMemo(() => {
        if (v2Payload?.explanation) {
            const items: { name: string; score: number }[] = [];
            const topExplanation = v2Payload.explanation as Record<string, unknown>;
            for (const sectionKey of resolveOrderedKeys(topExplanation, v2Payload.order)) {
                const sectionValue = topExplanation[sectionKey];
                if (!isScoredNode(sectionValue) || sectionValue.score == null) continue;
                const parsed = parseScore(sectionValue.score);
                if (Number.isNaN(parsed)) continue;
                items.push({ name: formatSectionTitle(sectionKey), score: parsed });
            }
            return items.length > 0 ? items : undefined;
        }
        const items: { name: string; score: number }[] = [];
        for (const key of Object.keys(requests?.[0]?.actions || {})) {
            const scores = (requests || [])
                .map((r: any) => parseScore(r?.actions?.[key]?.score))
                .filter((s: number) => !Number.isNaN(s));
            if (scores.length === 0) continue;
            const avg = scores.reduce((a: number, b: number) => a + b, 0) / scores.length;
            items.push({ name: formatActionName(key), score: avg });
        }
        return items.length > 0 ? items : undefined;
    }, [v2Payload, requests]);

    // Helper function to check if action matches filter
    const matchesFilter = (actionData: any, filter: 'matches' | 'partial' | 'mismatches' | null): boolean => {
        if (!filter) return true;
        const actionScore = actionData?.score;
        if (actionScore === undefined || actionScore === null) return false;
        const numScore = Math.round(parseScore(actionScore));
        if (filter === 'matches') return numScore >= 85;
        if (filter === 'partial') return numScore >= 60 && numScore < 85;
        if (filter === 'mismatches') return numScore < 60;
        return true;
    };

    // Helper function to recursively collect all searchable text from data
    const collectSearchableText = (data: any): string => {
        if (!data) return '';
        if (typeof data === 'string') return data.toLowerCase();
        if (typeof data === 'number' || typeof data === 'boolean') return String(data).toLowerCase();
        if (Array.isArray(data)) return data.map(item => collectSearchableText(item)).join(' ');
        if (typeof data === 'object' && data !== null) {
            return Object.entries(data).map(([key, value]) =>
                key.toLowerCase() + ' ' + collectSearchableText(value)
            ).join(' ');
        }
        return '';
    };

    // Helper function to parse search terms from query
    const getSearchTerms = (query: string): string[] => {
        return query.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0);
    };

    // Auto-expand request accordions that have matching actions when filter or search is applied
    useEffect(() => {
        if (v2Payload) return;
        const hasSearchQuery = searchQuery.trim().length > 0;

        // Helper to check if action matches search (uses global functions)
        const matchesSearchLocal = (actionKey: string, actionData: any, query: string): boolean => {
            if (!query.trim()) return true;
            const searchTerms = getSearchTerms(query);
            if (searchTerms.length === 0) return true;

            const metadata = actionMetadata[actionKey];
            const title = (metadata?.name || actionKey.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).toLowerCase();
            const subtitle = (metadata?.subtitle || '').toLowerCase();
            const explanationText = actionData?.explanation ? collectSearchableText(actionData.explanation) : '';
            const combinedText = `${title} ${subtitle} ${explanationText}`;

            return searchTerms.every(term => combinedText.includes(term));
        };

        if (!activeFilter && !hasSearchQuery) {
            // When both filter and search are cleared, collapse all except expand request 1 by default
            setSelectedKeys(prev => {
                const next = new Set(
                    Array.from(prev).filter(key =>
                        !key.startsWith('request-') &&
                        key !== 'supporting-information' &&
                        key !== 'policy-limit-check'
                    )
                );
                if (requests.length > 0) {
                    next.add('request-0');
                }
                return next;
            });
            setRequestNestedKeys(new Set());
            setSupportingNestedKeys(new Set());
            return;
        }

        const matchingRequestIndices = new Set<string>();
        const matchingRequestNestedKeys = new Set<string>();
        const matchingSupportingNestedKeys = new Set<string>();
        const isSearchOnly = hasSearchQuery && !activeFilter;

        requests.forEach((request: any, requestIndex: number) => {
            const actions = request?.actions || {};
            const hasMatchingAction = Object.entries(actions).some(([actionKey, actionData]: [string, any]) => {
                // Check filter match
                if (!matchesFilter(actionData, activeFilter)) return false;

                // Check search match
                if (hasSearchQuery) {
                    if (!matchesSearchLocal(actionKey, actionData, searchQuery)) return false;
                }

                return true;
            });

            if (hasMatchingAction) {
                matchingRequestIndices.add(`request-${requestIndex}`);

                // If search only (no filter), also expand matching nested accordions
                if (isSearchOnly) {
                    Object.entries(actions).forEach(([actionKey, actionData]: [string, any]) => {
                        if (matchesSearchLocal(actionKey, actionData, searchQuery)) {
                            matchingRequestNestedKeys.add(`request-${requestIndex}-${actionKey}`);
                        }
                    });
                }
            }
        });

        // Check if vehicle_owner_history / vehicle_history_report should be expanded
        let shouldExpandVehicleOwnerHistory = false;

        if (vehicleOwnerHistory?.explanation && typeof vehicleOwnerHistory.explanation === 'object') {
            Object.entries(vehicleOwnerHistory.explanation).forEach(([explanationKey, explanationData]: [string, any]) => {
                // Check filter match
                if (!matchesFilter(explanationData, activeFilter)) return;

                // Check search match
                if (hasSearchQuery) {
                    const searchTerms = getSearchTerms(searchQuery);
                    if (searchTerms.length > 0) {
                        const title = formatKeyForDisplay(explanationKey).toLowerCase();
                        const explanationText = explanationData?.explanation?.toLowerCase() || '';
                        const otherFieldsText = Object.entries(explanationData)
                            .filter(([key]) => key !== 'score' && key !== 'explanation')
                            .map(([key, value]) => `${key} ${collectSearchableText(value)}`)
                            .join(' ')
                            .toLowerCase();
                        const combinedText = `${title} ${explanationText} ${otherFieldsText}`;
                        if (!searchTerms.every(term => combinedText.includes(term))) return;
                    }
                }

                shouldExpandVehicleOwnerHistory = true;
            });
        }

        if (shouldExpandVehicleOwnerHistory) {
            matchingSupportingNestedKeys.add('vehicle-owner-history');
        }

        // Check if image scores should be expanded
        let shouldExpandAttachmentAnalysis = false;
        const matchingAttachmentKeys = new Set<string>();

        imageScores.forEach((imageScore: any, imageIndex: number) => {
            // Check filter match
            const imageMatchesFilter = matchesFilter({ score: imageScore?.score }, activeFilter);

            // Check search match
            const imgExplanationObj = (imageScore?.explanation && typeof imageScore.explanation === 'object' && !Array.isArray(imageScore.explanation)) ? imageScore.explanation : null;
            const imgResolvedDescription = imgExplanationObj?.description ?? imageScore?.description ?? '';
            const imgResolvedDetails = imgExplanationObj?.details ?? imageScore?.details ?? {};

            let imageMatchesSearch = true;
            if (hasSearchQuery) {
                const searchTerms = getSearchTerms(searchQuery);
                const title = `attachment ${imageIndex + 1}`.toLowerCase();
                const description = imgResolvedDescription.toLowerCase();
                const detailsText = collectSearchableText(imgResolvedDetails);
                const explanationText = imgExplanationObj ? collectSearchableText(imgExplanationObj) : '';
                const combinedText = `${title} ${description} ${detailsText} ${explanationText}`;
                imageMatchesSearch = searchTerms.every(term => combinedText.includes(term));
            }

            if (imageMatchesFilter && imageMatchesSearch && (activeFilter || hasSearchQuery)) {
                shouldExpandAttachmentAnalysis = true;
                // If search only (no filter), also expand matching individual attachment accordions
                if (isSearchOnly) {
                    const descriptionHash = imgResolvedDescription.substring(0, 20).replace(/\s+/g, '-') || 'empty';
                    const uniqueKey = `image-score-${imageIndex}-${imageScore?.score || 'no-score'}-${descriptionHash}-${JSON.stringify(imgResolvedDetails).substring(0, 10)}`;
                    matchingAttachmentKeys.add(uniqueKey);
                }
            }
        });

        if (shouldExpandAttachmentAnalysis) {
            matchingSupportingNestedKeys.add('attachment-analysis');
        }

        // Replace request keys with new matching ones and clear nested keys to avoid conflicts
        setSelectedKeys(prev => {
            const next = new Set(Array.from(prev).filter(key =>
                !key.startsWith('request-') &&
                key !== 'supporting-information' &&
                key !== 'policy-limit-check'
            ));

            matchingRequestIndices.forEach(k => next.add(k));
            // When a filter is selected, expand Supporting Information when it has any matching items (same behaviour for all filter buttons)
            if (matchingSupportingNestedKeys.size > 0) {
                next.add('supporting-information');
            }
            return next;
        });

        // Update nested keys
        setRequestNestedKeys(isSearchOnly ? matchingRequestNestedKeys : new Set());
        setSupportingNestedKeys(isSearchOnly ? matchingSupportingNestedKeys : new Set());
        setAttachmentAnalysisNestedKeys(isSearchOnly ? matchingAttachmentKeys : new Set());
    }, [activeFilter, requests, imageScores, vehicleOwnerHistory, policyLimitCheck, searchQuery, v2Payload]);

    // Early returns after all hooks
    if (!data) {
        return (
            <div className={`flex items-center justify-center py-12 ${className}`}>
                <div className="text-default-500">No claim data available</div>
            </div>
        );
    }

    if (!shouldRender) {
        return null;
    }

    // Helper function to check if score is empty
    const isEmptyScore = (score: any): boolean => {
        return score === null || score === undefined || score === '' || (typeof score === 'string' && score.trim() === '');
    };

    // Helper function to format objects and arrays
    const formatObjectOrArray = (score: any): string | null => {
        if (typeof score !== 'object' || score === null) {
            return null;
        }
        if (Array.isArray(score)) {
            return `[${score.length} items]`;
        }
        return JSON.stringify(score);
    };

    // Helper function to format date strings
    const formatDateScore = (score: string, keyLower: string): string | null => {
        if (!keyLower.includes('date') || typeof score !== 'string') {
            return null;
        }
        const dateValue = new Date(score);
        if (!isNaN(dateValue.getTime())) {
            return formatCoverageDate(score);
        }
        return score;
    };

    // Helper function to format numeric scores
    const formatNumericScore = (score: number, keyLower: string): string | number | null => {
        if (typeof score !== 'number' || Number.isNaN(score) || !Number.isFinite(score)) {
            return null;
        }
        if (keyLower.includes('year')) {
            return score;
        }
        return keyLower.includes('score')
            ? formatPercentage(Math.round(score) / 100)
            : formatNumber(score);
    };

    const formatScore = (score: any, key = '') => {
        if (isEmptyScore(score)) return '--';

        const isCurrencyKey = ((key.toLowerCase().includes('amount') || key.toLowerCase().includes('price') || key.toLowerCase().includes('cost')) && !key.toLowerCase().includes('percentage'))
            || TW_CURRENCY_KEYS.has(key);
        if (isCurrencyKey && (typeof score === 'number' || (typeof score === 'string' && !Number.isNaN(Number(score)) && score.trim() !== ''))) {
            return (
                <div key={key} className="renderer-key-value-block">
                    <div className="renderer-field-value text-default-500 mb-1">
                        {formatCurrency(Number(score))}
                    </div>
                </div>
            );
        }

        const objectOrArrayResult = formatObjectOrArray(score);
        if (objectOrArrayResult !== null) {
            return objectOrArrayResult;
        }

        const keyLower = key.toLowerCase();
        const dateResult = formatDateScore(score, keyLower);
        if (dateResult !== null) {
            return dateResult;
        }

        if (typeof score === 'boolean') {
            return score ? 'True' : 'False';
        }

        if (typeof score === 'string' || key.toLowerCase().includes('id')) {
            return score;
        }

        if (key.toLowerCase().includes('percent')) {
            return formatPercentage(Math.round(score) / 100);
        }

        const numericResult = formatNumericScore(score, keyLower);
        if (numericResult !== null) {
            return numericResult;
        }

        return score;
    };

    const collectLegacyExpandKeys = (
        mainKeys: Set<string>,
        nestedKeys: Set<string>,
        supportingKeys: Set<string>,
        attachmentKeys: Set<string>
    ) => {
        for (let requestIndex = 0; requestIndex < requests.length; requestIndex++) {
            mainKeys.add(`request-${requestIndex}`);
            const actions = requests[requestIndex]?.actions || {};
            for (const actionKey of Object.keys(actions)) {
                nestedKeys.add(`request-${requestIndex}-${actionKey}`);
            }
        }
        mainKeys.add('supporting-information');
        supportingKeys.add('attachment-analysis');

        for (let imageIndex = 0; imageIndex < imageScores.length; imageIndex++) {
            const imageScore = imageScores[imageIndex];
            const imgExpObj = (imageScore?.explanation && typeof imageScore.explanation === 'object' && !Array.isArray(imageScore.explanation)) ? imageScore.explanation : null;
            const descriptionHash = (imgExpObj?.description ?? imageScore?.description ?? '').substring(0, 20).replace(/\s+/g, '-') || 'empty';
            const uniqueKey = `image-score-${imageIndex}-${imageScore?.score || 'no-score'}-${descriptionHash}-${JSON.stringify(imgExpObj?.details ?? imageScore?.details ?? {}).substring(0, 10)}`;
            attachmentKeys.add(uniqueKey);
        }

        if (vehicleOwnerHistory) {
            supportingKeys.add('vehicle-owner-history');
            const explanation = normalizeVhrExplanation(vehicleOwnerHistory?.explanation);
            if (explanation) {
                for (const explanationKey of Object.keys(explanation)) {
                    nestedKeys.add(`vehicle-owner-history-${explanationKey}`);
                }
            }
        }
        if (policyLimitCheck) {
            mainKeys.add('policy-limit-check');
        }
    };

    const generateAllKeys = () => {
        const mainKeys = new Set<string>();
        const nestedKeys = new Set<string>();
        const supportingKeys = new Set<string>();
        const attachmentKeys = new Set<string>();

        if (aiSummary?.trim()) {
            mainKeys.add('ai-summary');
        }
        if (errorData) {
            mainKeys.add('error-details');
        }

        if (v2Payload?.explanation) {
            for (const { itemKey } of buildV2TopLevelAccordionEntries(
                v2Payload.explanation as Record<string, unknown>,
                v2Payload.order
            )) {
                mainKeys.add(itemKey);
            }
            return { mainKeys, nestedKeys, supportingKeys, attachmentKeys };
        }

        collectLegacyExpandKeys(mainKeys, nestedKeys, supportingKeys, attachmentKeys);
        return { mainKeys, nestedKeys, supportingKeys, attachmentKeys };
    };

    // Helper function to check if accordion matches search query
    const matchesSearch = (actionKey: string, actionData: any, query: string): boolean => {
        if (!query.trim()) return true;

        const searchTerms = getSearchTerms(query);
        if (searchTerms.length === 0) return true;

        // Collect all searchable text from this accordion
        const title = formatActionName(actionKey).toLowerCase();
        const subtitle = actionMetadata[actionKey]?.subtitle?.toLowerCase() || '';
        const explanationText = actionData?.explanation ? collectSearchableText(actionData.explanation) : '';
        const combinedText = `${title} ${subtitle} ${explanationText}`;

        // Check if ALL search terms are found
        return searchTerms.every(term => combinedText.includes(term));
    };

    // Helper function to render an action item
    const renderActionItem = (actionKey: string, actionData: any, itemKey: string, isNested = false) => {
        const { parsedScore, scoreDisplay } = parseAndFormatScore(actionData?.score);
        const scoreColor = parsedScore === null ? 'default' : getScoreColor(parsedScore);
        const metadata = actionMetadata[actionKey];
        const iconColor = metadata?.iconColor || 'text-default-500';
        const subtitle = metadata?.subtitle || '';

        // Map icon colors to background colors
        const getBackgroundColor = (iconColorClass: string) => {
            if (iconColorClass.includes('purple')) return 'bg-purple-100';
            if (iconColorClass.includes('blue')) return 'bg-blue-100';
            if (iconColorClass.includes('green')) return 'bg-green-100';
            if (iconColorClass.includes('orange')) return 'bg-orange-100';
            if (iconColorClass.includes('pink')) return 'bg-pink-100';
            return 'bg-default-100';
        };
        const bgColor = getBackgroundColor(iconColor);

        return (
            <AccordionItem
                key={itemKey}
                aria-label={formatActionName(actionKey)}
                title={
                    <div className="flex justify-between items-center w-full">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 ${bgColor} rounded-lg`}>
                                <Icon
                                    icon={metadata?.icon || 'mdi:information'}
                                    className={iconColor}
                                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                                />
                            </div>
                            <div>
                                <div className="renderer-text-primary font-semibold">{formatActionName(actionKey)}</div>
                                {subtitle && <div className="renderer-text-secondary">{subtitle}</div>}
                            </div>
                        </div>
                        <ActionScoreDisplay
                            parsedScore={parsedScore}
                            scoreColor={scoreColor}
                            scoreDisplay={scoreDisplay}
                        />
                    </div>
                }
                classNames={{
                    content: "pl-1 pr-0",
                    base: isNested ? "w-full !px-2 !shadow-none border-2" : "w-full",
                    title: isNested ? "text-sm" : "text-base"
                }}
                className="mb-3"
            >
                <div className="pt-2">
                    {actionData?.explanation ?
                        renderExplanationTable(actionData.explanation, actionKey, actionData) :
                        <div className="text-default-500">No explanation data available</div>
                    }
                </div>
            </AccordionItem>
        );
    };

    const handleExpandAll = () => {
        if (isExpanded) {
            setSelectedKeys(new Set());
            setRequestNestedKeys(new Set());
            setSupportingNestedKeys(new Set());
            setAttachmentAnalysisNestedKeys(new Set());
            setIsExpanded(false);
            return;
        }
        const { mainKeys, nestedKeys, supportingKeys, attachmentKeys } = generateAllKeys();
        const requestKeys = new Set(Array.from(nestedKeys).filter(key => key.startsWith('request-')));
        setSelectedKeys(mainKeys);
        setRequestNestedKeys(requestKeys);
        setSupportingNestedKeys(supportingKeys);
        setAttachmentAnalysisNestedKeys(attachmentKeys);
        setIsExpanded(true);
    };

    // Helper function to render array value (parentObject optional: used for msrp_sources to open C_&_K_source/forte_source modal)
    const renderArrayValue = (key: string, value: any[], level: number, parentObject?: Record<string, any>) => {
        const isMsrpSources = key === 'msrp_sources' || key === 'MSRP_sources';
        const ckSourceData = parentObject?.['C_&_K_source'] || parentObject?.['C&K_source'];
        const forteSourceData = parentObject?.['forte_source'];
        const tealSourceData = parentObject?.['Teal_source'];

        if (isMsrpSources) {
            if (!Array.isArray(value) || value.length === 0) return null;
            const msrpRowData: MsrpSourceGridRow[] = value.map(
                (row: { source?: string; value?: number; coreValue?: number }, idx: number) => {
                    const sourceRaw = row?.source ?? '';
                    const sl = sourceRaw.toLowerCase();
                    return {
                        rowId: idx,
                        sourceRaw,
                        sourceDisplay: sourceRaw.replace(/_/g, ' ') || '—',
                        isCkOem: sl.includes('c_&_k') || sl.includes('c&k'),
                        isForte: sl.includes('forte'),
                        isTealRow: sl.includes('teal'),
                        hasCkData: ckSourceData != null,
                        hasForteData: forteSourceData != null,
                        hasTealData: tealSourceData != null,
                        valueDisplay:
                            row.value != null && typeof row.value === 'number'
                                ? formatCurrency(row.value)
                                : (row.value as string | number | undefined) ?? '—',
                        coreValueDisplay:
                            row.coreValue != null && typeof row.coreValue === 'number'
                                ? formatCurrency(row.coreValue)
                                : (row.coreValue as string | number | undefined) ?? '—',
                    };
                }
            );
            const msrpColumnDefs: ColDef<MsrpSourceGridRow>[] = [
                {
                    field: 'sourceDisplay',
                    headerName: t('Source'),
                    flex: 1,
                    minWidth: 100,
                    cellRenderer: MsrpSourceAgGridCell,
                    cellClass: 'text-sm text-default-500',
                },
                {
                    field: 'valueDisplay',
                    headerName: t('Value'),
                    flex: 1,
                    minWidth: 100,
                    cellClass: 'text-sm text-default-500 whitespace-nowrap',
                },
                {
                    field: 'coreValueDisplay',
                    headerName: t('Core Value'),
                    flex: 1,
                    minWidth: 100,
                    cellClass: 'text-sm text-default-500 whitespace-nowrap',
                },
            ];
            return (
                <div key={key} className="py-2 border-b border-default-200 last:border-b-0 w-full">
                    <div className="renderer-field-name capitalize mb-2">
                        {formatKeyForDisplay(key)}
                    </div>
                    <div className="overflow-x-auto rounded border border-default-200 max-w-full doc-csg-msrp-grid-header-gray">
                        <style>{`
                          .doc-csg-msrp-grid-header-gray .ag-header,
                          .doc-csg-msrp-grid-header-gray .ag-header-cell {
                            background-color: rgb(229 231 235) !important;
                          }
                          .doc-csg-msrp-grid-header-gray .ag-layout-auto-height .ag-body-viewport,
                          .doc-csg-msrp-grid-header-gray .ag-layout-auto-height .ag-center-cols-viewport,
                          .doc-csg-msrp-grid-header-gray .ag-layout-auto-height .ag-center-cols-container {
                            min-height: 0 !important;
                          }
                        `}</style>
                        <div className="ag-theme-alpine w-full min-w-full" style={{ width: '100%' }}>
                            <AgGridReact<MsrpSourceGridRow>
                                rowData={msrpRowData}
                                columnDefs={msrpColumnDefs}
                                defaultColDef={GRID_DEFAULT_COL_DEF}
                                context={{
                                    setSourceJsonModal,
                                    ckSourceData,
                                    forteSourceData,
                                    tealSourceData,
                                }}
                                domLayout="autoHeight"
                                pagination={false}
                                suppressPaginationPanel
                                headerHeight={40}
                                rowHeight={40}
                                getRowId={(p) => String(p.data.rowId)}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={key} className="py-2 border-b border-default-200 last:border-b-0 w-full">
                <div className="renderer-field-name capitalize mb-2">
                    {formatKeyForDisplay(key)} :
                </div>
                {renderNestedObject(value, key, level + 1, key)}
            </div>
        );
    };

    const SKIPPED_RENDER_FIELD_KEYS = new Set(['score', 'clarity_score', 'validity_score']);
    const SKIPPED_RENDER_SOURCE_KEYS = new Set(['C_&_K_source', 'forte_source', 'C&K_source', 'Teal_source']);
    const CURRENCY_FIELD_TOKENS = ['amount', 'price', 'cost'] as const;

    const isNumericLikeValue = (value: any, requireNonEmptyTrimmedString = false): boolean => {
        if (typeof value === 'number') return true;
        if (typeof value !== 'string') return false;
        if (requireNonEmptyTrimmedString && value.trim() === '') return false;
        return !Number.isNaN(Number(value));
    };

    const isCurrencyField = (key: string): boolean => {
        const normalizedKey = key.toLowerCase();
        const containsCurrencyToken = CURRENCY_FIELD_TOKENS.some((token) => normalizedKey.includes(token));
        const isSpecialCurrencyField = normalizedKey === 'tire' || normalizedKey === 'wheel';
        return ((containsCurrencyToken || isSpecialCurrencyField) && !normalizedKey.includes('percentage'))
            || TW_CURRENCY_KEYS.has(key);
    };

    const isEmptyFieldValue = (value: any): boolean => {
        if (value == null || value === '') return true;
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === 'object') return Object.keys(value).length === 0;
        return false;
    };

    // Helper function to render field name and value
    const renderField = (key: string, value: any, showLabel = true) => {
        if (SKIPPED_RENDER_FIELD_KEYS.has(key) || SKIPPED_RENDER_SOURCE_KEYS.has(key)) {
            return null;
        }

        const isScoreKey = key.toLowerCase().includes('score');
        if (isScoreKey && isNumericLikeValue(value)) {
            return (
                <div key={key} className="flex justify-between items-center gap-2 py-1">
                    <span className="renderer-field-name capitalize">{formatKeyForDisplay(key)}</span>
                    {renderScoreChip(value)}
                </div>
            );
        }

        if (isCurrencyField(key) && isNumericLikeValue(value, true)) {
            return (
                <div key={key} className="renderer-key-value-block">
                    <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">
                        {formatKeyForDisplay(key)}
                    </div>
                    <div className="renderer-field-value text-default-500 mb-1">
                        {formatCurrency(Number(value))}
                    </div>
                </div>
            );
        }

        if (key === 'policy_terms') {
            return (
                <div key={key} className="mb-2">
                    <div className="renderer-section-title capitalize mb-2">
                        {formatKeyForDisplay(key)}
                    </div>
                    <div className="renderer-field-value">
                        {<MarkdownContent content={value} />}
                    </div>
                </div>
            );
        }

        if (isEmptyFieldValue(value)) {
            return (
                <div key={key} className="renderer-key-value-block">
                    {showLabel && (
                        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">
                            {formatKeyForDisplay(key)}
                        </div>
                    )}
                    <div className="renderer-field-value text-default-500 mb-1">
                        --
                    </div>
                </div>
            );
        }

        // Handle arrays and objects
        if (Array.isArray(value)) {
            return renderArrayValue(key, value, 0);
        }
        if (typeof value === 'object' && value !== null) {
            return renderNestedObjectBox(key, value, 0);
        }

        return (
            <div key={key} className="renderer-key-value-block">
                {showLabel && (
                    <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">
                        {formatKeyForDisplay(key)}
                    </div>
                )}
                <div className="renderer-field-value text-default-500 mb-1">
                    {formatScore(value, key)}
                </div>
            </div>
        );
    };

    // Helper function to render nested object in a box
    const renderNestedObjectBox = (key: string, value: any, level: number) => (
        <div key={key} className="py-2 border-b border-default-200 last:border-b-0 w-full">
            <div className=" p-1 rounded w-full">
                {renderNestedObject(value, key, level + 1, key, false)}
            </div>
        </div>
    );

    const renderNestedObject = (nestedData: any, title: string, level = 0, parentKey = '', isDirectChild = false): React.ReactNode => {
        if (!nestedData || typeof nestedData !== 'object') {
            return <span className="renderer-field-value">{String(nestedData)}</span>;
        }

        if (Array.isArray(nestedData)) {
            return (
                <div className="space-y-1 w-full overflow-x-auto">
                    {nestedData.map((item, idx) => (
                        <div key={`${parentKey}-${title}-${idx}`} className=" p-1 rounded w-full min-w-0">
                            {renderNestedObject(item, `${title} #${idx + 1}`, level + 1, title)}
                        </div>
                    ))}
                </div>
            );
        }

        const hasScore = nestedData.score !== undefined;
        const hasClarityScore = nestedData.clarity_score !== undefined;
        const hasValidityScore = nestedData.validity_score !== undefined;
        const nestedEntries = Object.entries(nestedData);
        const hasDirectValues = nestedEntries.some(([key, value]) => typeof value !== 'object');

        // For direct children of accordion items (like cause, complaint, correction), don't show the title again
        if (isDirectChild) {
            const messageId = nestedData?.message_id;
            const showPredictionIcon = messageId && typeof messageId === 'string';
            return (
                <div className="space-y-1">
                    {nestedEntries.map(([key, value]) => {
                        if (key === 'description' && typeof value === 'string') {
                            return <ClaimScoreHighlightField key={key} fieldKey={key} label="Description" value={value} />;
                        }
                        if(key==='message_id') return null;
                        if (SKIPPED_RENDER_SOURCE_KEYS.has(key)) return null;
                        if (Array.isArray(value)) {
                            return renderArrayValue(key, value, level, nestedData);
                        }
                        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                            return renderNestedObjectBox(key, value, level);
                        }
                        if (showPredictionIcon && key === 'predicted_loss_code') {
                            return (
                                <div key={key} className="renderer-key-value-block">
                                    <div className="renderer-field-name capitalize font-medium text-default-700 mb-1 flex items-center gap-1">
                                        {formatKeyForDisplay(key)}
                                        <PredictionExplainerInfoIcon messageId={messageId} />
                                    </div>
                                    <div className="renderer-field-value text-default-500 mb-1">
                                        {formatScore(value, key)}
                                    </div>
                                </div>
                            );
                        }
                        return renderField(key, value);
                    })}
                </div>
            );
        }

        if (!hasDirectValues && !hasScore && !hasClarityScore && !hasValidityScore && level > 0) {
            return (
                <>
                    {nestedEntries.map(([key, value]) => (
                        <div key={key} className="flex flex-col gap-2">
                            {typeof value === 'object' ?
                                renderNestedObject(value, key, level + 1, title) :
                                renderField(key, value, true)
                            }
                        </div>
                    ))}
                </>
            );
        }

        if (level > 0 && (hasDirectValues || hasScore || hasClarityScore || hasValidityScore)) {
            // Render as direct text with section header and score chip
            // Use section-title (smaller) for nested sections like "date_validation"
            return (
                <div className="w-full py-2">
                    {/* Section header with score */}
                    <div className="flex justify-between items-center mb-2">
                        <span className="renderer-section-title capitalize">{formatKeyForDisplay(title)}</span>
                        {(hasScore || hasClarityScore || hasValidityScore) && (() => {
                            const scoreValue = hasScore ? nestedData.score : hasClarityScore ? nestedData.clarity_score : nestedData.validity_score;
                            const scoreType = hasScore ? 'score' : hasClarityScore ? 'clarity_score' : 'validity_score';
                            const displayValue = Number.isFinite(scoreValue) ? formatScore(Math.round(scoreValue), scoreType) : scoreValue;
                            return (
                                <Chip
                                    color={getScoreColor(scoreValue)}
                                    variant="flat"
                                    size="sm"
                                >
                                    {displayValue}
                                </Chip>
                            );
                        })()}
                    </div>
                    {/* Direct content rendering */}
                    <div className="space-y-1 pl-1">
                        {renderNestedObject(nestedData, title, level + 1, parentKey, true)}
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-2">
                {nestedEntries.map(([key, value]) => {
                    if (key === 'description' && typeof value === 'string') {
                        return <ClaimScoreHighlightField key={key} fieldKey={key} label="Description" value={value} />;
                    }
                    if (SKIPPED_RENDER_SOURCE_KEYS.has(key)) return null;
                    if (typeof value === 'object' && !hasScore && !hasClarityScore && !hasValidityScore && !hasDirectValues) {
                        return (
                            <div key={key} className="flex flex-col gap-2">
                                {renderNestedObject(value, key, level + 1, key)}
                            </div>
                        );
                    }

                    if (Array.isArray(value)) {
                        return renderArrayValue(key, value, level, nestedData);
                    }
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        return renderNestedObjectBox(key, value, level);
                    }
                    return renderField(key, value);
                })}
            </div>
        );
    };

    const renderExplanationTable = (explanation: any, actionKey?: string, actionContext?: any) => {
        if (!explanation || typeof explanation !== 'object') {
            return <div className="text-default-500">{explanation}</div>;
        }

        // For repair_profile, render as sections like vehicle history report
        if (actionKey === 'repair_profile') {
            const repairProfileMessageId = explanation?.message_id ?? actionContext?.message_id;

            return (
                <div className="space-y-0">
                    {Object.entries(explanation)
                        .filter(([key]: [string, any]) => key !== 'message_id')
                        .map(([key, value]: [string, any], index: number, array: any[]) => {
                            if (typeof value !== 'object' || value === null) {
                                return renderField(key, value);
                            }

                            // Extract score if it exists in the value object
                            const { parsedScore, scoreDisplay } = parseAndFormatScore(value?.score);
                            const scoreColor = parsedScore === null ? 'default' : getScoreColor(parsedScore);

                            const nestedExpl = value?.explanation;
                            const useNestedExplanation =
                                nestedExpl != null &&
                                typeof nestedExpl === 'object' &&
                                !Array.isArray(nestedExpl);
                            const innerSource = useNestedExplanation ? nestedExpl : value;

                            return (
                                <div key={key} className={index < array.length - 1 ? "border-b border-default-200" : ""}>
                                    {/* Section Title with Score */}
                                    <div className="flex justify-between items-center mb-3 mt-3">
                                        <div className="renderer-text-primary capitalize font-semibold">
                                            {formatKeyForDisplay(key)}
                                        </div>
                                        {parsedScore !== null && (
                                            <ActionScoreDisplay
                                                parsedScore={parsedScore}
                                                scoreColor={scoreColor}
                                                scoreDisplay={scoreDisplay}
                                            />
                                        )}
                                    </div>
                                    {/* Section Content: legacy flat actual/predicted or nested value.explanation (e.g. actuals / predicted) */}
                                    <div className="space-y-1">
                                        {Object.entries(innerSource).map(([innerKey, innerValue]: [string, any]) => {
                                            if (innerKey === 'score') return null;
                                            if (innerKey === 'explanation') return null;
                                            if (innerKey === 'details' && Array.isArray(innerValue)) {
                                                return renderArrayValue(innerKey, innerValue, 0);
                                            }
                                            const sectionIsCurrency = /cost|amount|price/i.test(key);
                                            const effectiveKey = sectionIsCurrency && (innerKey === 'actual' || innerKey === 'actuals' || innerKey === 'predicted') ? key : innerKey;
                                            if (innerKey === 'predicted') {
                                                return (
                                                    <div key={innerKey} className="renderer-key-value-block">
                                                        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1 flex items-center gap-1">
                                                            {formatKeyForDisplay(innerKey)}
                                                            {repairProfileMessageId ? <PredictionExplainerInfoIcon messageId={repairProfileMessageId} /> : null}
                                                        </div>
                                                        <div className="renderer-field-value text-default-500 mb-1">
                                                            {formatScore(innerValue, effectiveKey)}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            if ((innerKey === 'actual' || innerKey === 'actuals') && sectionIsCurrency && typeof innerValue === 'number') {
                                                return (
                                                    <div key={innerKey} className="renderer-key-value-block">
                                                        <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">
                                                            {formatKeyForDisplay(innerKey)}
                                                        </div>
                                                        <div className="renderer-field-value text-default-500 mb-1">
                                                            {formatCurrency(innerValue)}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return renderField(innerKey, innerValue);
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            );
        }

        // ... existing code ...
        if (actionKey === 'cost_validation') {
            const sectionEntries = Object.entries(explanation).filter(([key, value]) =>
                key !== 'message_id' && typeof value === 'object' && value !== null
            ) as [string, { score?: number; explanation?: any[] | string | Record<string, any> } | any[]][];

            return (
                <div className="space-y-0">
                    <Accordion
                        variant="light"
                        isCompact
                        selectionMode="multiple"
                        className="gap-3 [&_hr]:hidden [&_.divider]:hidden [&_[role=separator]]:hidden"
                        dividerProps={{
                            className: "hidden"
                        }}
                    >
                        {sectionEntries.map(([arrayName, sectionData]) =>
                            renderCostValidationSection(arrayName, sectionData)
                        )}
                    </Accordion>
                </div>
            );
        }

        // Default rendering for other action types
        return (
            <div className="space-y-4">
                {Object.entries(explanation).map(([key, value]: [string, any]) => {
                    if (!value && value !== 0 && value !== false) return null;

                    if (typeof value !== 'object') {
                        return renderField(key, value);
                    }
                    const isRecallsAndTsbCheck = (sectionKey: string) =>
                        sectionKey.replace(/_/g, ' ').toLowerCase() === 'recalls and tsb check';

                    const innerExplanation = value?.explanation;
                    const isExplanationObject = typeof innerExplanation === 'object' && innerExplanation !== null && !Array.isArray(innerExplanation);
                    const useAccordions = isRecallsAndTsbCheck(key) && isExplanationObject;

                    if (useAccordions) {
                        const recalsTsbSectionScore = typeof value === 'object' && value !== null && value.score != null ? value.score : null;
                        const { parsedScore: parsedRecalsTsbScore, scoreDisplay: recalsTsbScoreDisplay } = parseAndFormatScore(recalsTsbSectionScore);
                        const recalsTsbScoreColor = parsedRecalsTsbScore === null ? 'default' : getScoreColor(parsedRecalsTsbScore);
                        return (
                            <div key={key} className="py-4 border-b border-default-200 last:border-b-0">
                                <div className="flex justify-between items-center gap-2 flex-wrap mb-3">
                                    <h4 className="renderer-section-subtitle capitalize">
                                        {formatKeyForDisplay(key)}
                                    </h4>
                                    {parsedRecalsTsbScore !== null && (
                                        <ActionScoreDisplay
                                            parsedScore={parsedRecalsTsbScore}
                                            scoreColor={recalsTsbScoreColor}
                                            scoreDisplay={recalsTsbScoreDisplay}
                                        />
                                    )}
                                </div>
                                <Accordion
                                    variant="light"
                                    isCompact
                                    selectionMode="multiple"
                                    className="gap-2 [&_hr]:hidden [&_.divider]:hidden"
                                    dividerProps={{ className: 'hidden' }}
                                >
                                    {Object.entries(innerExplanation).map(([innerKey, innerValue]: [string, any]) => {
                                        const innerScore = typeof innerValue === 'object' && innerValue !== null && innerValue.score != null ? innerValue.score : null;
                                        const { parsedScore: parsedInnerScore, scoreDisplay: innerScoreDisplay } = parseAndFormatScore(innerScore);
                                        const innerScoreColor = parsedInnerScore === null ? 'default' : getScoreColor(parsedInnerScore);
                                        return (
                                            <AccordionItem
                                                key={innerKey}
                                                aria-label={formatKeyForDisplay(innerKey)}
                                                title={
                                                    <div className="flex justify-between items-center gap-2 w-full">
                                                        <span className="text-[#667085] text-[14px] font-medium capitalize">
                                                            {formatKeyForDisplay(innerKey)}
                                                        </span>
                                                        {parsedInnerScore !== null && (
                                                            <ActionScoreDisplay
                                                                parsedScore={parsedInnerScore}
                                                                scoreColor={innerScoreColor}
                                                                scoreDisplay={innerScoreDisplay}
                                                            />
                                                        )}
                                                    </div>
                                                }
                                                classNames={{
                                                    content: 'pl-2 pr-0',
                                                    base: 'w-full !border-none !shadow-none !bg-transparent !px-0',
                                                    title: 'text-base w-full'
                                                }}
                                            >
                                                <div className="space-y-1 pt-1">
                                                    {typeof innerValue === 'object' && innerValue !== null
                                                        ? renderNestedObject(innerValue, innerKey, 0, actionKey)
                                                        : renderField(innerKey, innerValue)}
                                                </div>
                                            </AccordionItem>
                                        );
                                    })}
                                </Accordion>
                            </div>
                        );
                    }

                    // Render nested objects as direct text with section header
                    const sectionScore = typeof value === 'object' && value !== null && value.score != null ? value.score : null;
                    const { parsedScore: parsedSectionScore, scoreDisplay: sectionScoreDisplay } = parseAndFormatScore(sectionScore);
                    const sectionScoreColor = parsedSectionScore === null ? 'default' : getScoreColor(parsedSectionScore);
                    return (
                        <div key={key} className="py-4 border-b border-default-200 last:border-b-0">
                            <div className="flex justify-between items-center gap-2 flex-wrap">
                                <h4 className="renderer-section-subtitle capitalize">
                                    {formatKeyForDisplay(key)}
                                </h4>
                                {parsedSectionScore !== null && (
                                    <ActionScoreDisplay
                                        parsedScore={parsedSectionScore}
                                        scoreColor={sectionScoreColor}
                                        scoreDisplay={sectionScoreDisplay}
                                    />
                                )}
                            </div>
                            <div className="space-y-1 pl-1 mt-3">
                                {renderNestedObject(value, key, 0, actionKey)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const resetStopGateExpansion = () => {
        setActiveStopGate(null);
        setStopGateScrollTargetId(null);
        setSelectedKeys(new Set());
        setRequestNestedKeys(new Set());
        setHasInitializedExpansion(false);
    };

    const handleStopGateClick = (gate: string) => {
        if (activeStopGate === gate) {
            resetStopGateExpansion();
            return;
        }
        setActiveFilter(null);
        setSearchQuery('');
        setActiveStopGate(gate);
        if (!v2Payload) return;
        const info = resolveV2StopGateInfo(v2Payload, gate);
        setSelectedKeys(new Set(info.topItemKey ? [info.topItemKey] : []));
        setRequestNestedKeys(new Set(info.nestedExpandKeys));
        setStopGateScrollTargetId(info.scrollTargetId);
    };

    const handleFilterClick = (filterType: 'matches' | 'partial' | 'mismatches') => {
        const newFilter = activeFilter === filterType ? null : filterType;
        setActiveFilter(newFilter);
        if (activeStopGate) {
            resetStopGateExpansion();
        }
    };

    const vehicleHistoryExplanationItemMatchesSearch = (explanationKey: string, explanationData: any): boolean => {
        const matchesFilterType = matchesFilter(explanationData, activeFilter);
        if (!matchesFilterType) return false;
        if (!searchQuery.trim()) return true;
        const searchTerms = getSearchTerms(searchQuery);
        if (searchTerms.length === 0) return true;
        const title = explanationKey.replace(/_/g, ' ').toLowerCase();
        const isObj = typeof explanationData === 'object' && explanationData !== null;
        const explanationText = isObj ? (explanationData?.explanation?.toLowerCase() || '') : '';
        const otherFieldsText = isObj
            ? Object.entries(explanationData)
                .filter(([key]) => key !== 'score' && key !== 'explanation')
                .map(([key, value]) => `${key} ${collectSearchableText(value)}`)
                .join(' ')
                .toLowerCase()
            : String(explanationData ?? '').toLowerCase();
        const combinedText = `${title} ${explanationText} ${otherFieldsText}`;
        return searchTerms.every(term => combinedText.includes(term));
    };

    const renderVehicleHistoryExplanationDataEntries = (explanationData: any, explanationKey: string) => {
        if (typeof explanationData !== 'object' || explanationData === null) {
            return renderField(explanationKey, explanationData);
        }
        return Object.entries(explanationData).map(([key, value]: [string, any]) => {
            if (key === 'score') return null;
            if (key === 'details' && Array.isArray(value)) {
                return renderArrayValue(key, value, 0);
            }
            return renderField(key, value);
        });
    };

    const renderVehicleHistoryRootDataEntries = (root: Record<string, any>) =>
        Object.entries(root).map(([key, value]: [string, any]) => {
            if (key === 'score') return null;
            return renderField(key, value);
        });

    const renderVehicleHistoryExplanationEntry = (
        explanationKey: string,
        explanationData: any,
    ) => {
        const { parsedScore: parsedItemScore, scoreDisplay: itemScoreDisplay } = parseAndFormatScore(
            typeof explanationData === 'object' && explanationData !== null ? explanationData?.score : undefined
        );
        const itemScoreColor = parsedItemScore === null ? 'default' : getScoreColor(parsedItemScore);
        return (
            <details
                key={`vhr-${explanationKey}`}
                id={`vhr-explanation-${explanationKey}`}
                className="vhr-nested-explanation w-full border-b border-default-200 last:border-b-0 [&_summary::-webkit-details-marker]:hidden"
            >
                <summary className="flex w-full cursor-pointer list-none items-center gap-2 py-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <div className="flex min-w-0 items-center gap-1 whitespace-nowrap">
                            <span className="text-[#667085] text-[14px] capitalize">{formatKeyForDisplay(explanationKey)}</span>
                            <span
                                className="vhr-chevron-rotate h-4 w-4 shrink-0 text-[#667085]"
                                aria-hidden
                            >
                                <Icon icon="mdi:chevron-down" className="h-4 w-4" aria-hidden />
                            </span>
                        </div>
                        <div className="min-w-0 flex-1 border-b border-default-300"></div>
                        {parsedItemScore !== null && (
                            <ActionScoreDisplay
                                parsedScore={parsedItemScore}
                                scoreColor={itemScoreColor}
                                scoreDisplay={itemScoreDisplay}
                            />
                        )}
                    </div>
                </summary>
                <div className="space-y-1 pl-1 pb-2">
                    {renderVehicleHistoryExplanationDataEntries(explanationData, explanationKey)}
                </div>
            </details>
        );
    };

    const renderVehicleOwnerHistoryAccordionInnerContent = (owner: Record<string, any>) => {
        const vhrExplanationMap = normalizeVhrExplanation(owner?.explanation);
        if (vhrExplanationMap && Object.keys(vhrExplanationMap).length > 0) {
            const filteredEntries: [string, any][] = [];
            for (const [explanationKey, explanationData] of Object.entries(vhrExplanationMap)) {
                if (vehicleHistoryExplanationItemMatchesSearch(explanationKey, explanationData)) {
                    filteredEntries.push([explanationKey, explanationData]);
                }
            }
            const explanationNodes: React.ReactNode[] = [];
            for (const [explanationKey, explanationData] of filteredEntries) {
                explanationNodes.push(
                    renderVehicleHistoryExplanationEntry(explanationKey, explanationData)
                );
            }
            return (
                <div className="flex flex-col gap-0 vhr-nested-entries">
                    <style>{`
                                    .vhr-nested-entries .vhr-nested-explanation .vhr-chevron-rotate {
                                        display: inline-flex;
                                        align-items: center;
                                        justify-content: center;
                                        transform: rotate(0deg);
                                        transform-origin: 50% 50%;
                                        transition: transform 0.3s ease-in-out;
                                    }
                                    .vhr-nested-entries .vhr-nested-explanation[open] .vhr-chevron-rotate {
                                        transform: rotate(180deg);
                                    }
                                    @media (prefers-reduced-motion: reduce) {
                                        .vhr-nested-entries .vhr-nested-explanation .vhr-chevron-rotate {
                                            transition: none;
                                        }
                                    }
                                `}</style>
                    {explanationNodes}
                </div>
            );
        }

        const rootRows = renderVehicleHistoryRootDataEntries(owner);
        let hasRootContent = false;
        for (const n of rootRows) {
            if (n != null) {
                hasRootContent = true;
                break;
            }
        }
        if (!hasRootContent) {
            return (
                <div className="text-center py-8 text-default-500">
                    <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                    <p className="text-sm">No vehicle history report data available</p>
                </div>
            );
        }
        return <div className="space-y-1">{rootRows}</div>;
    };

    const renderSrtAgGridTable = (key: string, value: any[]) => {
        if (!Array.isArray(value) || value.length === 0) return null;
        const srtRowData: SrtSourceGridRow[] = value.map((row: any, idx: number) => {
            const sourceRaw = row?.source ?? '';
            const infoObj = row?.info;
            const hasInfo = infoObj != null && typeof infoObj === 'object' && Object.keys(infoObj).length > 0;
            return {
                rowId: idx,
                sourceDisplay: sourceRaw.replace(/_/g, ' ') || '—',
                srtDisplay: row?.SRT == null ? '—' : String(row.SRT),
                hasInfo,
                infoData: hasInfo ? infoObj : null,
            };
        });
        const srtColumnDefs: ColDef<SrtSourceGridRow>[] = [
            {
                field: 'sourceDisplay',
                headerName: t('Source'),
                flex: 1,
                minWidth: 140,
                cellRenderer: SrtSourceAgGridCell,
                cellClass: 'text-sm text-default-500',
            },
            {
                field: 'srtDisplay',
                headerName: t('SRT'),
                flex: 1,
                minWidth: 100,
                cellClass: 'text-sm text-default-500 whitespace-nowrap',
            },
        ];
        return (
            <div key={key} className="py-2 border-b border-default-200 last:border-b-0 w-full">
                <div className="renderer-field-name capitalize mb-2">
                    {formatKeyForDisplay(key)}
                </div>
                <div className="overflow-x-auto rounded border border-default-200 max-w-full doc-csg-msrp-grid-header-gray">
                    <style>{`
                      .doc-csg-msrp-grid-header-gray .ag-header,
                      .doc-csg-msrp-grid-header-gray .ag-header-cell {
                        background-color: rgb(229 231 235) !important;
                      }
                      .doc-csg-msrp-grid-header-gray .ag-layout-auto-height .ag-body-viewport,
                      .doc-csg-msrp-grid-header-gray .ag-layout-auto-height .ag-center-cols-viewport,
                      .doc-csg-msrp-grid-header-gray .ag-layout-auto-height .ag-center-cols-container {
                        min-height: 0 !important;
                      }
                    `}</style>
                    <div className="ag-theme-alpine w-full min-w-0 max-w-full" style={{ width: '100%' }}>
                        <AgGridReact<SrtSourceGridRow>
                            rowData={srtRowData}
                            columnDefs={srtColumnDefs}
                            defaultColDef={GRID_DEFAULT_COL_DEF}
                            context={{ setSourceJsonModal }}
                            domLayout="autoHeight"
                            pagination={false}
                            suppressPaginationPanel
                            headerHeight={40}
                            rowHeight={40}
                            getRowId={(p) => String(p.data.rowId)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderCostValidationItemEntries = (item: any) =>
        Object.entries(item).map(([key, value]: [string, any]) => {
            if (key === 'score') return null;
            if (SKIPPED_RENDER_SOURCE_KEYS.has(key)) return null;
            if (key === 'details' && Array.isArray(value)) {
                return renderArrayValue(key, value, 0);
            }
            if (Array.isArray(value) && (key === 'MSRP_sources' || key === 'msrp_sources')) {
                return renderArrayValue(key, value, 0, item);
            }
            if (Array.isArray(value) && key.toLowerCase().startsWith('srt')) {
                return renderSrtAgGridTable(key, value);
            }
            return renderField(key, value);
        });

    const renderCostValidationArrayItem = (
        item: any,
        itemIndex: number,
        arrayValue: any[],
        arrayName: string,
        displayArrayName: string,
        entryKeyLabel?: string
    ) => {
        const { parsedScore: parsedItemScore, scoreDisplay: itemScoreDisplay } = parseAndFormatScore(item?.score);
        const itemScoreColor = parsedItemScore === null ? 'default' : getScoreColor(parsedItemScore);
        const baseName = displayArrayName === 'Parts' ? 'Part' : displayArrayName;
        const entryKeyLabelMissing = entryKeyLabel === undefined || entryKeyLabel === null || (typeof entryKeyLabel === 'string' && entryKeyLabel.trim() === '');
        let isEntryKeyLabelNumber = false;
        if (entryKeyLabelMissing) {
            isEntryKeyLabelNumber = true;
        } else if (typeof entryKeyLabel === 'number') {
            isEntryKeyLabelNumber = Number.isFinite(entryKeyLabel);
        } else if (typeof entryKeyLabel === 'string') {
            isEntryKeyLabelNumber = Number.isFinite(Number(entryKeyLabel));
        }
        const sectionTitle = isEntryKeyLabelNumber ? `${baseName} #${itemIndex + 1}` : `${baseName} #${itemIndex + 1} (${entryKeyLabel})`;
        return (
            <div
                key={`${arrayName}-${itemIndex}`}
                className={itemIndex < arrayValue.length - 1 ? 'border-b border-default-200' : ''}
            >
                <div className="flex justify-between items-center mb-3 mt-3">
                    <div className="renderer-text-primary capitalize font-semibold">
                        {sectionTitle}
                    </div>
                    {parsedItemScore !== null && (
                        <ActionScoreDisplay
                            parsedScore={parsedItemScore}
                            scoreColor={itemScoreColor}
                            scoreDisplay={itemScoreDisplay}
                        />
                    )}
                </div>
                <div className="space-y-1">
                    {renderCostValidationItemEntries(item)}
                </div>
            </div>
        );
    };

    const renderDynamicSectionContent = (sectionKey: string, sectionValue: any) => {
        if (typeof sectionValue === 'object' && sectionValue !== null && !Array.isArray(sectionValue)) {
            return (
                <div className="space-y-1">
                    {Object.entries(sectionValue).map(([k, v]: [string, any]) => renderField(k, v))}
                </div>
            );
        }
        if (Array.isArray(sectionValue)) {
            return renderArrayValue(sectionKey, sectionValue, 0);
        }
        return <div className="text-sm text-default-700 pb-4">{String(sectionValue)}</div>;
    };

    const renderCostValidationSectionBody = (
        arrayValue: any[],
        objectEntries: [string, any][],
        arrayName: string,
        displayArrayName: string,
        fallbackText: string,
    ) => {
        if (arrayValue.length > 0) {
            return arrayValue.map((item: any, itemIndex: number) =>
                renderCostValidationArrayItem(item, itemIndex, arrayValue, arrayName, displayArrayName)
            );
        }
        if (objectEntries.length > 0) {
            return objectEntries.map(([entryKey, entryValue]: [string, any], entryIdx: number) => {
                const entryItem = typeof entryValue === 'object' && entryValue !== null ? entryValue : { value: entryValue };
                return renderCostValidationArrayItem(
                    entryItem, entryIdx, objectEntries.map(([, v]) => v), arrayName, displayArrayName, entryKey
                );
            });
        }
        return <p className="text-sm text-default-500 py-2">{fallbackText}</p>;
    };

    const renderCostValidationSection = (arrayName: string, sectionData: any) => {
        const isDirectArray = Array.isArray(sectionData);
        const explanationValue = isDirectArray ? sectionData : (sectionData.explanation ?? []);
        const isArrayExplanation = Array.isArray(explanationValue);
        const isObjectExplanation = !isDirectArray && !isArrayExplanation && typeof explanationValue === 'object' && explanationValue !== null;
        const arrayValue = isArrayExplanation ? explanationValue : [];
        const arrayScore = isDirectArray ? null : sectionData.score ?? null;
        const { parsedScore: parsedArrayScore, scoreDisplay: arrayScoreDisplay } = parseAndFormatScore(arrayScore);
        const arrayScoreColor = parsedArrayScore === null ? 'default' : getScoreColor(parsedArrayScore);

        const displayArrayName = arrayName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        const objectEntries = isObjectExplanation
            ? Object.entries(explanationValue as Record<string, any>)
            : [];

        return (
            <AccordionItem
                key={arrayName}
                aria-label={displayArrayName}
                title={
                    <div className="flex items-center w-full gap-2">
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-[#667085] text-[14px] font-semibold">{displayArrayName}</span>
                            <Icon
                                icon="mdi:chevron-down"
                                className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180"
                            />
                        </div>
                        <div className="flex-1 border-b border-default-300"></div>
                        {parsedArrayScore !== null && (
                            <ActionScoreDisplay
                                parsedScore={parsedArrayScore}
                                scoreColor={arrayScoreColor}
                                scoreDisplay={arrayScoreDisplay}
                            />
                        )}
                    </div>
                }
                indicator={<></>}
                classNames={{
                    content: "pl-0 pr-0",
                    base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                    title: "text-base w-full",
                    trigger: "group py-2"
                }}
                className="mb-1 [&>h2>button]:block"
            >
                <div>
                    <div className="space-y-0">
                        {renderCostValidationSectionBody(arrayValue, objectEntries, arrayName, displayArrayName, String(sectionData?.explanation ?? ''))}
                    </div>
                </div>
            </AccordionItem>
        );
    };

    const renderMainContent = () => {
        const { scoreValue, displayScore } = getAgentScoreDisplay(v2Payload?.score ?? agentScore);
        const scoreColor = getScoreColor(scoreValue);

        const renderVehicleOwnerHistoryAccordionItem = () => {
            if (!vehicleOwnerHistory || (activeFilter && !matchesFilter(vehicleOwnerHistory, activeFilter))) return null;
            return (
                <AccordionItem
                    key="vehicle-owner-history"
                    aria-label="Vehicle Owner History"
                    title={
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <Icon icon="mdi:medal" className="text-purple-600" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                                </div>
                                <div>
                                    <div className="renderer-text-primary font-semibold">
                                        Vehicle History Report
                                    </div>
                                    <div className="renderer-text-secondary">Vehicle historical data</div>
                                </div>
                            </div>
                            {vehicleOwnerHistory?.score !== undefined && vehicleOwnerHistory?.score !== null && renderScoreChip(vehicleOwnerHistory.score)}
                        </div>
                    }
                    classNames={{
                        content: "pl-1 pr-0",
                        base: "w-full !px-2 !shadow-none border-2",
                        title: "text-base"
                    }}
                    className="mb-3"
                >
                    <div className="pt-2">
                        {renderVehicleOwnerHistoryAccordionInnerContent(vehicleOwnerHistory)}
                    </div>
                </AccordionItem>
            );
        };

        let detailsContent: React.ReactNode = null;
        if (sorInspection) {
            detailsContent = (
                <VirtualInspectionSorDetails
                    definition={sorInspection.definition}
                    inspectionId={sorInspection.inspectionId}
                    agentName={agentName}
                    searchQuery={searchQuery}
                    activeFilter={activeFilter}
                    stopGatePath={stopGatePath}
                    nestedSelectedKeys={requestNestedKeys}
                    onNestedSelectedKeysChange={setRequestNestedKeys}
                    timestamp={timestamp}
                    setSourceJsonModal={setSourceJsonModal}
                    selectedKeys={selectedKeys}
                    onSelectionChange={(keys) => setSelectedKeys(keys as Set<string>)}
                />
            );
        } else if (v2Payload) {
            detailsContent = renderV2TopLevelBlocks(v2TopBlocks, {
                selectedKeys,
                onSelectionChange: (keys) => setSelectedKeys(keys as Set<string>),
            });
        }

        const overviewContent = (
            <>
                {/* Search Bar */}
                <div className="mb-4 px-2">
                    <Input
                        type="text"
                        placeholder="Search by field name, value, or notes..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (activeStopGate) {
                                resetStopGateExpansion();
                            }
                        }}
                        startContent={<Icon icon="mdi:magnify" className="text-default-400" style={{ width: '18px', height: '18px' }} />}
                        size="sm"
                        classNames={{
                            input: "text-sm",
                            inputWrapper: "border-default-300 h-8"
                        }}
                    />
                </div>

                {isVirtualInspection && (() => {
                    const message = sorInspection
                        ? readVirtualInspectionMessage(data?.action_output, v2Payload)
                        : (v2Payload as { meta_data?: { message?: unknown } } | null)?.meta_data?.message;
                    const state = data?.state;
                    const chips: string[] = [];
                    if (typeof message === 'string' && message.trim()) chips.push(message.trim());
                    else if (typeof message === 'number') chips.push(String(message));
                    if (typeof state === 'string' && state.trim()) chips.push(formatCamelCaseLabel(state.trim()));
                    else if (typeof state === 'number') chips.push(String(state));
                    if (chips.length === 0) return null;
                    return (
                        <div className="flex flex-wrap gap-2 mb-4 px-2">
                            {chips.map((value, idx) => (
                                <Chip
                                    key={`${idx}-${value}`}
                                    variant="flat"
                                    size="sm"
                                    classNames={{
                                        base: "!bg-content3 border border-divider",
                                        content: "!text-foreground",
                                    }}
                                >
                                    {value}
                                </Chip>
                            ))}
                        </div>
                    );
                })()}

                <Accordion
                    variant="splitted"
                    selectionMode="multiple"
                    selectedKeys={selectedKeys}
                    onSelectionChange={(keys) => setSelectedKeys(keys as Set<string>)}
                    isCompact
                    className="gap-2 [&_hr]:hidden [&_.divider]:hidden"
                >
                    {aiSummary?.trim() ? (
                    <AccordionItem
                        key="ai-summary"
                        aria-label="AI Summary"
                        title={
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-pink-100 rounded-lg">
                                    <Icon icon="mdi:sparkles" className="text-pink-600" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                                </div>
                                <div>
                                    <div className="renderer-text-primary font-semibold">AI Summary</div>
                                    <div className="renderer-text-secondary">AI-generated analysis overview</div>
                                </div>
                            </div>
                        }
                        className="mb-3"
                        classNames={{
                            content: "pl-1 pr-0",
                            base: "w-full !px-2 !shadow-none border-2",
                            title: "text-base"
                        }}
                    >
                        <div className="pt-1">
                            <MarkdownContent content={aiSummary} />
                        </div>
                    </AccordionItem>
                    ) : null}

                    {/* Show error if exists */}
                    {errorData && (
                        <AccordionItem
                            key="error-details"
                            aria-label="Error Details"
                            title={
                                <div className="flex items-center gap-3">
                                    <Icon icon="mdi:alert-circle" className="w-5 h-5 text-red-600" />
                                    <span className="font-medium text-red-600">Error</span>
                                </div>
                            }
                            className="mb-3"
                        >
                            <div className="pt-2">
                                <ErrorDisplay errorData={errorData} />
                            </div>
                        </AccordionItem>
                    )}

                    {!v2Payload && requests.map((request: any, requestIndex: number) => {
                        const actions = request?.actions || {};

                        return (
                            <AccordionItem
                                key={`request-${requestIndex}`}
                                aria-label={`Request ${requestIndex + 1}`}
                                title={
                                    <div className="flex items-center w-full gap-2">
                                        <div className="flex items-center gap-1 whitespace-nowrap">
                                            <span className="text-[#667085] text-[14px]">Request {requestIndex + 1}</span>
                                            <Icon
                                                icon="mdi:chevron-down"
                                                className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180"
                                            />
                                        </div>
                                        <div className="flex-1 border-b border-default-300"></div>
                                        {request?.request_score !== undefined && request?.request_score !== null && renderScoreChip(request.request_score)}
                                    </div>
                                }
                                indicator={<></>}
                                classNames={{
                                    content: "pl-0 pr-0",
                                    base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                                    title: "text-base w-full",
                                    trigger: "group py-2"
                                }}
                                className="[&>h2>button]:block"
                            >
                                <div className="pt-2">
                                    <Accordion
                                        variant="light"
                                        isCompact
                                        selectionMode="multiple"
                                        selectedKeys={requestNestedKeys}
                                        onSelectionChange={(keys) => {
                                            setRequestNestedKeys(keys as Set<string>);
                                        }}
                                        className="gap-3 [&_hr]:hidden [&_.divider]:hidden [&_[role=separator]]:hidden"
                                        dividerProps={{
                                            className: "hidden"
                                        }}
                                    >
                                        {Object.entries(actions).length > 0 ? (
                                            Object.entries(actions)
                                                .filter(([actionKey, actionData]) =>
                                                    matchesFilter(actionData, activeFilter) && matchesSearch(actionKey, actionData, searchQuery)
                                                )
                                                .map(([actionKey, actionData]: [string, any]) =>
                                                    renderActionItem(actionKey, actionData, `request-${requestIndex}-${actionKey}`, true)
                                                )
                                        ) : (
                                            <AccordionItem
                                                key="no-actions"
                                                aria-label="No Actions"
                                                title={
                                                    <div className="flex items-center gap-3">
                                                        <Icon icon="mdi:information-outline" className="w-5 h-5 text-default-500" />
                                                        <span className="font-medium text-default-500">No Actions Available</span>
                                                    </div>
                                                }
                                                className="mb-3"
                                            >
                                                <div className="text-center py-8 text-default-500">
                                                    <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                                                    <p className="text-sm">No action data available for this request</p>
                                                </div>
                                            </AccordionItem>
                                        )}
                                    </Accordion>
                                </div>
                            </AccordionItem>
                        );
                    })}

                    {!v2Payload && policyLimitCheck && (!activeFilter || matchesFilter(policyLimitCheck, activeFilter)) && (
                        <AccordionItem
                            key="policy-limit-check"
                            aria-label="Policy Limit Check"
                            title={
                                <div className="flex items-center w-full gap-2">
                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                        <span className="text-[#667085] text-[14px] font-semibold">T&W Check</span>
                                        <Icon icon="mdi:chevron-down" className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180" />
                                    </div>
                                    <div className="flex-1 border-b border-default-300"></div>
                                    {policyLimitCheck?.score !== undefined && policyLimitCheck?.score !== null && renderScoreChip(policyLimitCheck.score)}
                                </div>
                            }
                            indicator={<></>}
                            classNames={{
                                content: "pl-1 pr-0",
                                base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                                title: "text-base w-full",
                                trigger: "group py-2"
                            }}
                            className="[&>h2>button]:block"
                        >
                            <div className="pt-2 border border-default-300 rounded-lg p-2 border-2">
                                {policyLimitCheck?.explanation ? (
                                    typeof policyLimitCheck.explanation === 'object' ? (
                                        <div className="space-y-1">
                                            {Object.entries(policyLimitCheck.explanation).map(([key, value]: [string, any]) => (
                                                renderField(key, value)
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-default-700 pb-4">
                                            {String(policyLimitCheck.explanation)}
                                        </div>
                                    )
                                ) : (
                                    <div className="text-center py-8 text-default-500">
                                        <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                                        <p className="text-sm">No T&W Check data available</p>
                                    </div>
                                )}
                            </div>
                        </AccordionItem>
                    )}

                    {!v2Payload && extraTopLevelSections.map(({ key: sectionKey, value: sectionValue }) => {
                        const sectionScore = typeof sectionValue === 'object' && sectionValue !== null && !Array.isArray(sectionValue) ? sectionValue.score : undefined;
                        return (
                            <AccordionItem
                                key={`extra-${sectionKey}`}
                                aria-label={formatKeyForDisplay(sectionKey)}
                                title={
                                    <div className="flex items-center w-full gap-2">
                                        <div className="flex items-center gap-1 whitespace-nowrap">
                                            <span className="text-[#667085] text-[14px] font-semibold">{formatKeyForDisplay(sectionKey)}</span>
                                            <Icon icon="mdi:chevron-down" className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180" />
                                        </div>
                                        <div className="flex-1 border-b border-default-300"></div>
                                        {sectionScore !== undefined && sectionScore !== null && renderScoreChip(sectionScore)}
                                    </div>
                                }
                                indicator={<></>}
                                classNames={{
                                    content: "pl-1 pr-0",
                                    base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                                    title: "text-base w-full",
                                    trigger: "group py-2"
                                }}
                                className="[&>h2>button]:block"
                            >
                                <div className="pt-2 border border-default-300 rounded-lg p-2 border-2">
                                    {renderDynamicSectionContent(sectionKey, sectionValue)}
                                </div>
                            </AccordionItem>
                        );
                    })}

                    {!v2Payload && agentName.toLowerCase().includes('vhr') && renderVehicleOwnerHistoryAccordionItem()}

                    {!v2Payload && !sorInspection && !agentName.toLowerCase().includes('vhr') && (() => {
                        const hasSupportingContent = (vehicleOwnerHistory)
                            || (imageScores && imageScores.length > 0);
                        return (
                            <AccordionItem
                                key="supporting-information"
                                aria-label="Supporting Information"
                                title={
                                    <div className="flex items-center w-full gap-2">
                                        <div className="flex items-center gap-1 whitespace-nowrap">
                                            <span className="text-[#667085] text-[14px]">Supporting Information</span>
                                            <Icon
                                                icon="mdi:chevron-down"
                                                className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180"
                                            />
                                        </div>
                                        <div className="flex-1 border-b border-default-300"></div>
                                    </div>
                                }
                                indicator={<></>}
                                classNames={{
                                    content: "pl-0 pr-0",
                                    base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                                    title: "text-base w-full",
                                    trigger: "group py-2"
                                }}
                                className="[&>h2>button]:block"
                            >
                                {hasSupportingContent ? (
                                    <div className="pt-2">
                                        <Accordion
                                            variant="light"
                                            isCompact
                                            selectionMode="multiple"
                                            selectedKeys={supportingNestedKeys}
                                            onSelectionChange={(keys) => setSupportingNestedKeys(keys as Set<string>)}
                                            className="gap-3 [&_hr]:hidden [&_.divider]:hidden [&_[role=separator]]:hidden"
                                            dividerProps={{
                                                className: "hidden"
                                            }}
                                        >
                                            {/* Vehicle Owner History (Policy Limit Check is now top-level above) */}
                                            {renderVehicleOwnerHistoryAccordionItem()}

                                            {/* Attachment Analysis Grouping */}
                                            {imageScores && imageScores.length > 0 && (!activeFilter || matchesFilter({ score: attachmentAnalysisScore }, activeFilter)) && (
                                                <AccordionItem
                                                    key="attachment-analysis"
                                                    aria-label="Attachment Analysis"
                                                    title={
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 bg-green-50 rounded-lg">
                                                                    <Icon icon="mdi:image-multiple" className="text-green-600" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                                                                </div>
                                                                <div>
                                                                    <div className="renderer-text-primary font-bold">Attachment Analysis</div>
                                                                    <div className="renderer-text-secondary">Attached review</div>
                                                                </div>
                                                            </div>
                                                            {attachmentAnalysisScore !== null && renderScoreChip(attachmentAnalysisScore)}
                                                        </div>
                                                    }
                                                    classNames={{
                                                        content: "pl-1 pr-0",
                                                        base: "w-full !px-2 !shadow-none border-2",
                                                        title: "text-base"
                                                    }}
                                                    className="mb-3"
                                                >
                                                    <div className="pt-2">
                                                        <Accordion
                                                            variant="light"
                                                            isCompact
                                                            selectionMode="multiple"
                                                            selectedKeys={attachmentAnalysisNestedKeys}
                                                            onSelectionChange={(keys) => setAttachmentAnalysisNestedKeys(keys as Set<string>)}
                                                            className="gap-3 [&_hr]:hidden [&_.divider]:hidden [&_[role=separator]]:hidden"
                                                            dividerProps={{
                                                                className: "hidden"
                                                            }}
                                                        >
                                                            {imageScores
                                                                .filter((imageScore: any) => {
                                                                    const matchesFilterType = matchesFilter({ score: imageScore?.score }, activeFilter);
                                                                    if (!matchesFilterType) return false;

                                                                    if (searchQuery.trim()) {
                                                                        const searchTerms = getSearchTerms(searchQuery);
                                                                        const title = `attachment ${imageScores.indexOf(imageScore) + 1}`.toLowerCase();
                                                                        const expObj = (imageScore?.explanation && typeof imageScore.explanation === 'object' && !Array.isArray(imageScore.explanation)) ? imageScore.explanation : null;
                                                                        const description = (expObj?.description ?? imageScore?.description ?? '').toLowerCase();
                                                                        const detailsText = collectSearchableText(expObj?.details ?? imageScore?.details ?? {});
                                                                        const explanationText = expObj ? collectSearchableText(expObj) : '';
                                                                        const combinedText = `${title} ${description} ${detailsText} ${explanationText}`;
                                                                        return searchTerms.every(term => combinedText.includes(term));
                                                                    }
                                                                    return true;
                                                                })
                                                                .map((imageScore: any, filteredIndex: number) => {
                                                                    // We need the original index for the key and title
                                                                    const originalIndex = imageScores.indexOf(imageScore);
                                                                    const score = imageScore?.score;
                                                                    const expObj = (imageScore?.explanation && typeof imageScore.explanation === 'object' && !Array.isArray(imageScore.explanation)) ? imageScore.explanation : null;
                                                                    const descriptionHash = (expObj?.description ?? imageScore?.description ?? '').substring(0, 20).replace(/\s+/g, '-') || 'empty';
                                                                    const uniqueKey = `image-score-${originalIndex}-${score || 'no-score'}-${descriptionHash}-${JSON.stringify(expObj?.details ?? imageScore?.details ?? {}).substring(0, 10)}`;
                                                                    const explanationObj = (imageScore?.explanation && typeof imageScore.explanation === 'object' && !Array.isArray(imageScore.explanation))
                                                                        ? imageScore.explanation
                                                                        : null;
                                                                    const resolvedDescription = explanationObj?.description ?? imageScore?.description;
                                                                    const resolvedDetails = explanationObj?.details ?? imageScore?.details;
                                                                    const resolvedMetadata = explanationObj?.metadata ?? imageScore?.metadata;
                                                                    const resolved3cResults = explanationObj?.['3c_relevance_results'] ?? imageScore?.['3c_relevance_results'];
                                                                    const resolvedTranscript = imageScore?.transcript;
                                                                    const resolvedAiGenPercentage = imageScore?.ai_generation_percentage;
                                                                    const resolvedAiAuthScore = imageScore?.ai_auth_score;
                                                                    const resolvedExplanationString = (!explanationObj && typeof imageScore?.explanation === 'string') ? imageScore.explanation : null;
                                                                    const handledExplanationKeys = new Set(['details', 'metadata', 'description', '3c_relevance_results']);
                                                                    const extraExplanationEntries: [string, any][] = explanationObj
                                                                        ? Object.entries(explanationObj).filter(([k]) => !handledExplanationKeys.has(k))
                                                                        : [];
                                                                    const hasContent = resolvedDescription || resolvedDetails || imageScore?.source_id || resolvedMetadata || (resolved3cResults?.length > 0) || explanationObj;
                                                                    const sourceFileExtension = imageScore?.filename?.includes('.') ? imageScore?.filename?.split('.').pop()?.toLowerCase() : '';
                                                                    const isVideoAttachment = isVideoExtension(sourceFileExtension);
                                                                    const isAudioAttachment = isAudioExtension(sourceFileExtension);
                                                                    const imageScoreContent = !hasContent ? (
                                                                        <div className="text-center py-8 text-default-500">
                                                                            <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                                                                            <p className="text-sm">{typeof imageScore?.explanation === 'string' ? imageScore.explanation : t('No data available')}</p>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            {imageScore?.source_id ? (
                                                                                <div className="mb-4">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        {imageScore?.type}
                                                                                    </h4>
                                                                                    {imageScorePresignedUrls[imageScore.source_id] && (
                                                                                        <AttachmentMediaWithFallback
                                                                                            url={imageScorePresignedUrls[imageScore.source_id]}
                                                                                            alt={imageScore?.filename || `Attachment ${originalIndex + 1}`}
                                                                                            isVideo={isVideoAttachment}
                                                                                            isAudio={isAudioAttachment}
                                                                                        />
                                                                                    )}
                                                                                    {presignedUrlsFetchFailed && (
                                                                                        <div className="flex items-center justify-center rounded-lg border border-default-200 bg-content2 min-h-[120px] gap-2 text-default-500">
                                                                                            <Icon icon="mdi:alert-circle-outline" className="w-8 h-8 text-default-400" aria-hidden />
                                                                                            <p className="text-sm">{t('Attachment could not be loaded')}</p>
                                                                                        </div>
                                                                                    )}
                                                                                    {isLoadingAgentPresignedUrls && (
                                                                                        <div className="flex items-center justify-center rounded-lg border border-default-200  min-h-[120px]">
                                                                                            <Spinner size="sm" aria-label={t('Loading')} />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        {imageScore?.type}
                                                                                    </h4>
                                                                                    <div className="flex items-center justify-center rounded-lg border border-default-200 bg-content2 min-h-[120px] gap-2 text-default-500">
                                                                                        <Icon icon="mdi:alert-circle-outline" className="w-8 h-8 text-default-400" aria-hidden />
                                                                                        <p className="text-sm">{t('Attachment Not Available')}</p>
                                                                                    </div>
                                                                                </>
                                                                            )}
                                                                            {resolvedDescription && (
                                                                                <div className="mb-2">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        Description
                                                                                    </h4>
                                                                                    <div className="renderer-field-value">
                                                                                        <MarkdownContent content={resolvedDescription} />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {resolvedExplanationString && (
                                                                                <div className="mb-2">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        Explanation
                                                                                    </h4>
                                                                                    <div className="renderer-field-value">
                                                                                        <MarkdownContent content={resolvedExplanationString} />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {resolvedTranscript && (
                                                                                <div className="mb-2">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        Transcript
                                                                                    </h4>
                                                                                    <div className="renderer-field-value">
                                                                                        <MarkdownContent content={resolvedTranscript} />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {resolvedDetails && (
                                                                                <div className="py-4">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-3">
                                                                                        Details
                                                                                    </h4>
                                                                                    <div className="space-y-1 pl-1">
                                                                                        {Object.entries(resolvedDetails).map(([key, value]) =>
                                                                                            key.toLowerCase().includes('score') ? (
                                                                                                <div key={key} className="flex justify-between items-center gap-2 py-1">
                                                                                                    <span className="renderer-field-name capitalize">{formatKeyForDisplay(key)}</span>
                                                                                                    {renderScoreChip(value)}
                                                                                                </div>
                                                                                            ) : (
                                                                                                renderField(key, value)
                                                                                            )
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            <div className="mb-2">
                                                                                <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                    Date and Time
                                                                                </h4>
                                                                                {resolvedMetadata?.DateTime ? (
                                                                                    <div className="renderer-field-value">
                                                                                        {conversationHistoryTimezone(resolvedMetadata.DateTime.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3"))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="renderer-field-value">
                                                                                        <p>{t('Not Available')}</p>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            {resolvedAiGenPercentage && (
                                                                                <div className="mb-2">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        AI Generation Percentage
                                                                                    </h4>
                                                                                    {resolvedAiGenPercentage}
                                                                                </div>
                                                                            )}
                                                                            {resolvedAiAuthScore && (
                                                                                <div className="mb-2">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-2">
                                                                                        AI Auth Score
                                                                                    </h4>
                                                                                    {resolvedAiAuthScore}
                                                                                </div>
                                                                            )}
                                                                            {resolved3cResults?.length > 0 && (
                                                                                <div className="py-4 border-b border-default-200 last:border-b-0">
                                                                                    <h4 className="renderer-section-subtitle capitalize mb-3">
                                                                                        {t('3C Relevance Results')}
                                                                                    </h4>
                                                                                    <div className="space-y-3 pl-1">
                                                                                        {resolved3cResults.map((result: any, idx: number) => (
                                                                                            <div
                                                                                                key={`3c-relevance-${originalIndex}-${result?.['3c_relevance_score'] ?? ''}-${(result?.justification?.summary ?? '').slice(0, 30)}`}
                                                                                                className="border border-default-200 rounded p-3 /50"
                                                                                            >
                                                                                                {renderNestedObject(result, `Result ${idx + 1}`, 0, '3c_relevance')}
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            <div className="py-4 border-b border-default-200 last:border-b-0">
                                                                                <h4 className="renderer-section-subtitle capitalize mb-3">
                                                                                    {t('Geo Location')}
                                                                                </h4>
                                                                                {(resolvedMetadata?.modified_GPS?.latitude && resolvedMetadata?.modified_GPS?.longitude) ? (
                                                                                    renderMap(resolvedMetadata)
                                                                                ) : (
                                                                                    <div className="text-center py-8 text-default-500 bg-default-100 rounded-lg  ">
                                                                                        <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                                                                                        <p className="text-sm">{t('Geo Location Not Available')}</p>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            {extraExplanationEntries.length > 0 && (
                                                                                <div className="py-2 space-y-1">
                                                                                    {extraExplanationEntries.map(([key, value]) => renderField(key, value))}
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );

                                                                    return (
                                                                        <AccordionItem
                                                                            key={uniqueKey}
                                                                            aria-label={`Attachment ${originalIndex + 1}`}
                                                                            title={
                                                                                <div className="flex items-center w-full gap-2">
                                                                                    <div className="flex items-center gap-1 whitespace-nowrap">
                                                                                        <span className="text-[#667085] text-[14px]">Attachment {filteredIndex + 1}</span>
                                                                                        <Icon
                                                                                            icon="mdi:chevron-down"
                                                                                            className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="flex-1 border-b border-default-300"></div>
                                                                                    {imageScore?.score !== undefined && imageScore?.score !== null && renderScoreChip(imageScore.score)}
                                                                                </div>
                                                                            }
                                                                            indicator={<></>}
                                                                            classNames={{
                                                                                content: "pl-0 pr-0",
                                                                                base: "w-full !border-none !shadow-none !bg-transparent !px-0",
                                                                                title: "text-base w-full",
                                                                                trigger: "group py-2"
                                                                            }}
                                                                            className="mb-1 [&>h2>button]:block"
                                                                        >
                                                                            <div className="pt-2">
                                                                                {imageScoreContent}
                                                                            </div>
                                                                        </AccordionItem>
                                                                    );
                                                                })}
                                                        </Accordion>
                                                    </div>
                                                </AccordionItem>
                                            )}
                                        </Accordion>
                                    </div>
                                ) : (
                                    <div className="pt-2 border border-default-300 rounded-lg p-2 border-2">
                                        <div className="text-center py-8 text-default-500">
                                            <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                                            <p className="text-sm">{t('No supporting information available')}</p>
                                        </div>
                                    </div>
                                )}
                            </AccordionItem>
                        );
                    })()}
                </Accordion>

                {detailsContent}

            </>
        );

        const inspectionState = isVirtualInspection ? data?.state : undefined;
        const showInspectionReviewActions = typeof inspectionState === 'string'
            && inspectionState.replace(/\s/g, '').toLowerCase() === 'inreview';

        return (
            <div className={`flex flex-col h-full claim-score-generator ${className}`} style={{ '--accordion-divider-display': 'none' } as React.CSSProperties}>
                {/* Header Section */}
                <div className="border-b-3 border-default-200 bg-default-100 m-2 rounded-md">
                    <RendererHeader
                        agentName={agentName}
                        duration={duration}
                        entityNumber={entityNumber}
                        timestamp={timestamp}
                        isModal={isModal}
                        onFullScreenClick={() => onRequestFullScreen ? onRequestFullScreen() : setIsFullScreenOpen(true)}
                        onRefresh={onRefresh}
                    />

                    {/* Score Card */}
                    {shouldRender && (
                        <div className="flex-none pl-3 pr-3">
                            <ScoreCard
                                displayScore={displayScore}
                                scoreValue={scoreValue}
                                scoreColor={scoreColor}
                                matchBreakdown={matchBreakdown}
                                activeFilter={activeFilter}
                                onFilterClick={handleFilterClick}
                                feedbackComponent={feedbackComponent}
                                scoreCalculationItems={scoreCalculationItems}
                                stopGates={stopGateItems}
                                activeStopGate={activeStopGate}
                                onStopGateClick={handleStopGateClick}
                            />
                        </div>
                    )}
                </div>

                {/* Tabs Section */}
                <RendererTabs
                    activeTab={activeTab}
                    onTabChange={(key) => setActiveTab(key)}
                    isExpanded={isExpanded}
                    onExpandAll={handleExpandAll}
                    knowledgeComponent={sourcesContent}
                    overviewContent={overviewContent}
                    showSourcesTab={showSourcesTab}
                />

                {showInspectionReviewActions && (
                    <div className="flex-none flex gap-2 p-3 border-t border-divider bg-content1">
                        <button
                            type="button"
                            disabled={inspectionReviewPending != null}
                            onClick={() => { void handleInspectionReview('Completed'); }}
                            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-3 py-2 text-sm font-semibold !text-white disabled:opacity-60 dark:bg-[#16a34a] dark:text-white"
                        >
                            {inspectionReviewPending === 'Completed' && (
                                <Icon icon="mdi:loading" className="h-4 w-4 animate-spin" aria-hidden />
                            )}
                            {t('Complete Inspection')}
                        </button>
                        <button
                            type="button"
                            disabled={inspectionReviewPending != null}
                            onClick={() => { void handleInspectionReview('Cancelled'); }}
                            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-default-200 bg-content1 px-3 py-2 text-sm font-semibold text-danger disabled:opacity-60"
                        >
                            {inspectionReviewPending === 'Cancelled' && (
                                <Icon icon="mdi:loading" className="h-4 w-4 animate-spin" aria-hidden />
                            )}
                            {t('Cancel Request')}
                        </button>
                    </div>
                )}

                {showActionButtons && data?.action_button && data.action_button.length > 0 && (
                    <div className="flex-none p-3 border-t border-default-200 ">
                        <div className="flex gap-3 justify-center">
                            {data.action_button.map((button: any, index: number) => (
                                <button
                                    key={index}
                                    onClick={() => onButtonAction?.(button.action || 'click', button)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    {button.label || button.name || `Action ${index + 1}`}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {renderMainContent()}

            {/* Fullscreen Modal for Overview Tab */}
            <FullScreenModal
                isOpen={isFullScreenOpen}
                onClose={() => setIsFullScreenOpen(false)}
                agentName={agentName}
                modalClassName="claim-score-generator"
                headerBorder={true}
            >
                <ClaimScoreGeneratorRenderer
                    claimData={claimData}
                    onButtonAction={onButtonAction}
                    showActionButtons={showActionButtons}
                    knowledgeComponent={knowledgeComponent}
                    feedbackComponent={feedbackComponent}
                    isModal={true}
                    source={source}
                    onRefresh={onRefresh}
                />
            </FullScreenModal>

            {/* Fullscreen Modal for Template Preview */}
            <Modal
                isOpen={isTemplateFullScreenOpen}
                onOpenChange={setIsTemplateFullScreenOpen}
                size="5xl"
                hideCloseButton
                classNames={{
                    base: "max-w-[90vw] max-h-[95vh]",
                    body: "p-0",
                    header: "border-b border-default-200"
                }}
                scrollBehavior="inside"
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <Icon icon="mdi:file-code" className="text-primary" width={28} />
                                        <div>
                                            <h2 className="text-xl font-semibold">{agentName}</h2>
                                            <p className="text-sm text-default-500 font-normal">Fullscreen Preview</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            isIconOnly
                                            variant="light"
                                            onClick={onClose}
                                        >
                                            <Icon icon="mdi:close" className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>
                            </ModalHeader>
                            <ModalBody className="p-2">
                                {renderTemplatePreview()}
                            </ModalBody>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Template Modal (70vw x 70vh or fullscreen) */}
            <Modal
                isOpen={isTemplateModalOpen}
                onOpenChange={(open) => {
                    setIsTemplateModalOpen(open);
                    if (!open) setIsTemplateModalFullscreen(false);
                }}
                hideCloseButton
                size={isTemplateModalFullscreen ? "full" : undefined}
                classNames={{
                    wrapper: isTemplateModalFullscreen ? "p-0 w-screen h-screen max-w-none max-h-none min-w-full min-h-full" : undefined,
                    base: isTemplateModalFullscreen
                        ? "w-full h-full max-w-none max-h-none rounded-none m-0 flex flex-col"
                        : "max-w-[70vw] max-h-[70vh]",
                    body: isTemplateModalFullscreen ? "flex-1 min-h-0 overflow-auto p-0" : "p-0",
                    header: "border-b border-default-200"
                }}
                scrollBehavior="inside"
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-3">
                                        <Icon icon="mdi:file-code" className="text-primary" width={28} />
                                        <div>
                                            <h2 className="text-xl font-semibold">{agentName}</h2>
                                            <p className="text-sm text-default-500 font-normal">
                                                {receivedClaimData?.claim_no ? `Claim: ${receivedClaimData.claim_no}` : 'Preview'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isLoadingTemplateData && <Spinner size="sm" />}
                                        <Tooltip content={isTemplateModalFullscreen ? t('Minimize') : t('Fullscreen')} placement="bottom" showArrow>
                                            <Button
                                                isIconOnly
                                                variant="light"
                                                size="sm"
                                                onClick={() => setIsTemplateModalFullscreen((prev) => !prev)}
                                                aria-label={isTemplateModalFullscreen ? t('Minimize') : t('Fullscreen')}
                                            >
                                                <Icon icon={isTemplateModalFullscreen ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'} className="w-5 h-5" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip content={t('Open in new tab')} placement="bottom" showArrow>
                                            <Button isIconOnly variant="light" size="sm" onClick={openTemplateInNewTab} aria-label={t('Open in new tab')}>
                                                <Icon icon="mdi:open-in-new" className="w-5 h-5" />
                                            </Button>
                                        </Tooltip>
                                        <Tooltip content={t('Download PDF')} placement="bottom" showArrow>
                                            <Button isIconOnly variant="light" size="sm" onPress={() => checkDownloadPdf(() => downloadTemplateAsPdf())} isDisabled={isDownloadingPdf || isCheckingDownloadPdf} aria-label={t('Download PDF')}>
                                                {(isDownloadingPdf || isCheckingDownloadPdf) ? <Spinner size="sm" /> : <Icon icon="mdi:file-pdf-box" className="w-5 h-5" />}
                                            </Button>
                                        </Tooltip>
                                        {!isTemplateModalFullscreen && (
                                            <Button isIconOnly variant="light" onClick={onClose} aria-label={t('Close')}>
                                                <Icon icon="mdi:close" className="w-5 h-5" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </ModalHeader>
                            <ModalBody className={isTemplateModalFullscreen ? "p-2 flex-1 min-h-0 overflow-auto flex flex-col" : "p-2"}>
                                <div ref={templatePreviewRef} className={isTemplateModalFullscreen ? "flex-1 min-h-0 h-full overflow-auto flex flex-col" : undefined}>
                                    {renderTemplatePreview(isTemplateModalFullscreen)}
                                </div>
                            </ModalBody>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Source detail modal (C_&_K_source / forte_source / Teal_source / generic JSON) */}
            <SourceJsonModal
                state={sourceJsonModal}
                onClose={() => setSourceJsonModal(null)}
                timestamp={timestamp}
            />
        </>
    );
};

export { ClaimScoreGeneratorRenderer };
export default ClaimScoreGeneratorRenderer; 